import { formatMessageDateTime } from "./messageTime.js?v=45.0.0"
import {
    completedChatIdentitiesFromMessages,
    consumeChunkedRealtimeUpdate,
    durableChatResponseId,
    escapePlaintextHtml,
    hasChatSequenceGap,
    isStaleInFlightChatUpdate,
    resolveTerminalActionState,
    sanitizeRichHtml,
    shouldHydrateInFlightChatText,
    shouldInterceptReferenceClick,
    writeClipboardContent,
} from "./chatFrontendGuards.js?v=1.0.2"
import { createSlowToolProgressController } from "./slowToolProgress.js?v=1.0.2"

const CANONICAL_CHAT_RECOVERY_TIMEOUT_MS = 10000;
const CHAT_RECOVERY_POLL_TIMEOUT_MS = 10000;
const CHAT_RECOVERY_SILENCE_MS = 30000;

export default {
    inject: {
        captureDocumentResponseViewport: { default: null },
        restoreDocumentResponseViewport: { default: null },
    },
    props: {
        message: Object,
        chatUuid: { type: String, default: '' },
        responseAudioKey: { type: String, default: '' },
        messagesCount: {
            type: Number,
            required: true
        },
        replying: {
            type: Boolean,
            default: false
        },
        is_call_log: {
            type: Boolean,
            default: false
        },
        collaborative: {
            type: Boolean,
            default: false
        },
        lastCompletedRunId: {
            type: [String, Number],
            default: null
        },
        readyArtifactHandles: { type: Array, default: () => [] },
        artifactProjectionReady: {
            type: Boolean,
            default: false
        },
        hasDocumentArtifact: {
            type: Boolean,
            default: false
        },
        hasDocumentOutput: { type: Boolean, default: false },
        hasLiveDocumentPreview: { type: Boolean, default: false },
        sourceReceipts: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            count: 5,
            copyFeedback: false,
            documentResponseWasPending: false,
            documentResponseHadLivePreview: false,
            documentResponseCompletedHere: false,
            documentResponseReadyTimer: null,
            copyInProgress: false,
            copyError: '',
            downloadFeedback: false,
            downloadDocxFeedback: false,
            artifactActionWarningVisible: false,
            popupPosition: { top: 0, left: 0 },
            currentHoveredLink: null,
            isHoveringPopup: false,
            hidePopupTimeout: null,
            leavePopupTimeout: null,
            isShowingPopup: false,
            hoverTimeout: null,
            persistentPopupTrigger: null,
            researchActivity: null,
            researchInterval: null,
            slowToolProgressController: null,
            slowToolProgressMessage: '',
            slowToolProgressKey: '',
            hasReceivedInitialResponse: false,
            suppressInitialResponseDetection: false,
            hasReceivedRealtimeActivity: false,
            hasReceivedChatRealtimeActivity: false,
            lastLivenessSignalType: null,
            lastLivenessAt: null,
            progressWaitStartedAt: Date.now(),
            lastProgressAt: null,
            lastWorkerHeartbeatAt: null,
            showDelayedResponseNotice: false,
            // Ably channel for this message
            ablyChannel: null,
            researchMessageListener: null,
            researchStateListener: null,
            // Research activity history navigation
            // Only a response that is actively being generated starts with
            // its research details open. Restored/completed responses stay
            // compact until the user explicitly expands them.
            showResearchHistory: Boolean(this.message && (
                this.message.isLoading === true
                || (
                    this.message.waitingForUser === true
                    && this.message.resumingContext === true
                )
            )),
            selectedResearchEntryId: '',
            followLatest: true,
            // Chat updates via Ably
            chatChannel: null,
            chatMessageListener: null,
            chatFailedStateListener: null,
            chatSuspendedStateListener: null,
            chatDetachedStateListener: null,
            chatUpdateTimeout: null,
            chatSequence: 0,
            chatChunkState: { groups: {}, completedIds: [] },
            activeRunId: null,
            CHAT_UPDATE_TIMEOUT_MS: 200000, // Generation stall threshold, separate from recovery polling.
            CHAT_PHASE_TIMEOUT_SECONDS: {
                pre_stream: 90,
                context_loading: 90,
                compaction: 90,
                request_preparing: 120,
                provider_waiting: 330,
                first_stream_waiting: 330,
            },
            CHAT_HEARTBEAT_FRESH_SECONDS: 36,
            CHAT_HEARTBEAT_GRACE_SECONDS: 330,
            isReceivingViaAbly: false,
            ablyRetryCount: 0,
            researchSubscriptionAttempt: 0,
            chatSubscriptionAttempt: 0,
            recoveryPollTimer: null,
            recoveryPollController: null,
            recoveryPollingActive: false,
            recoveryPollAttempts: 0,
            canonicalRecoveryInFlight: null,
            reasoningSeedRequested: false,
            reasoningSeedInFlight: false,
            lastResearchActivitySeq: 0,
            researchGapRecoveryInFlight: false,
            continuationReasoningRecoveryTimer: null,
            continuationReasoningRecoveryActive: false,
            CONTINUATION_REASONING_RECOVERY_MS: 3000,
            initialUpdateGraceTimeout: null,
            INITIAL_UPDATE_GRACE_MS: CHAT_RECOVERY_SILENCE_MS,
            // Add reasoning during writing detection variables
            isReasoningDuringChat: false,
            lastReasoningActivityTime: 0,
            reasoningIndicatorTimeout: null,
            latestCheckInterval: null,
            // Resume timeout for when chat is resumed in loading state
            resumeTimeout: null,
            isResumedLoadingState: false,
            // Stop generation state
            stopping: false,
            // Cached handler for lexiusdoc button events
            boundLexiusDocButtonHandler: null,
            boundCloseDetailsHandler: null,
            ablyConnectionHandlers: null,
        }
    },
    computed: {
        liveDocumentResponsePending() {
            return this.documentResponseState === 'pending' && this.hasLiveDocumentPreview;
        },
        documentResponseState() {
            if (!this.hasDocumentOutput || this.message.cancelled || this.message.failed
                || this.message.waitingForUser) return 'inactive';
            if (this.message.isLoading) return 'pending';
            return this.displayText.trim() ? 'ready' : 'inactive';
        },
        documentResponseLayoutKey() {
            return [this.documentResponseState, this.showResearchHistory, this.documentResponseCompletedHere,
                this.documentResponseState === 'pending' ? '' : this.renderedText].join('\u001f');
        },
        responseAudioComplete() {
            return !this.message.isLoading && !this.message.waitingForUser
                && !this.message.cancelled && !this.message.failed;
        },
        displayText() {
            const originalText = this.message && this.message.text ? this.message.text : '';
            if (!this.readyArtifactHandles.length) {
                return originalText;
            }
            return originalText.replace(
                /<!--\s*LEXIUS_ARTIFACT_COMPAT_START:([^>]*?)\s*-->[\s\S]*?<!--\s*LEXIUS_ARTIFACT_COMPAT_END\s*-->/gi,
                (block, handle) => this.readyArtifactHandles.includes(handle.trim()) ? '' : block
            ).replace(/\n{3,}/g, '\n\n').trim();
        },
        renderedText() {
            // Always render markdown, even during loading to show streaming content
            // Empty text will be handled by the template logic

            // Use the shared markdown processing function from summarizers.js
            const baseText = this.stripLexiusDocFilenameHint(this.displayText);
            let processedMarkdown = processMarkdownContent(baseText);

            try {
                let html = marked.parse(processedMarkdown);

                // Add special styling for [INTERRUPCIÓN] text (BotMessage-specific)
                html = html.replace(/\[INTERRUPCIÓN\]/g, '<span class="interrupcion-text">[INTERRUPCIÓN]</span>');

                // Helpers can insert HTML (including document block content),
                // so sanitize the final transformed result immediately before
                // it reaches v-html.
                const result = processConvertedHTML(html, baseText);
                return this.addResponseViewportAnchors(
                    sanitizeRichHtml(result, baseText),
                );
            } catch (error) {
                return escapePlaintextHtml(baseText);
            }
        },

        lexiusDocBlocks() {
            if (typeof extractLexiusDocBlocks === 'function') {
                const baseText = this.stripLexiusDocFilenameHint(this.displayText);
                return extractLexiusDocBlocks(baseText) || [];
            }
            return [];
        },

        suggestedFileName() {
            return this.getSuggestedFileName();
        },

        isMobileDevice() {
            return typeof window !== 'undefined' && 'ontouchstart' in window;
        },

        processedResearchActivityMessage() {
            if (!this.researchActivity || !this.researchActivity.message) {
                return '';
            }

            // Process the research activity message through marked.parse
            try {
                return sanitizeRichHtml(
                    marked.parse(this.researchActivity.message),
                    this.researchActivity.message,
                );
            } catch (error) {
                return escapePlaintextHtml(this.researchActivity.message);
            }
        },

        // Research activity history computed properties
        hasResearchHistory() {
            return this.researchHistoryItems.length > 0;
        },

        researchHistoryItems() {
            const activities = this.message && Array.isArray(this.message.researchActivityHistory)
                ? this.message.researchActivityHistory.map((activity, index) => ({
                    kind: 'activity',
                    id: this.researchActivityEntryId(activity),
                    status: activity && activity.status,
                    activity,
                    timelineTimestamp: this.researchEntryTimestamp(
                        activity && (activity.timestamp || activity.created_at)
                    ),
                    timelineOrder: index,
                }))
                : [];
            const sources = Array.isArray(this.sourceReceipts)
                ? this.sourceReceipts.map((receipt, index) => ({
                    kind: 'source',
                    id: this.researchSourceEntryId(receipt),
                    status: 'source',
                    receipt,
                    timelineTimestamp: this.researchEntryTimestamp(receipt && receipt.created_at),
                    timelineOrder: activities.length + index,
                }))
                : [];
            return [...activities, ...sources].sort((left, right) => {
                const leftTimestamp = left.timelineTimestamp;
                const rightTimestamp = right.timelineTimestamp;
                if (leftTimestamp !== null && rightTimestamp !== null && leftTimestamp !== rightTimestamp) {
                    return leftTimestamp - rightTimestamp;
                }
                if (leftTimestamp !== null && rightTimestamp === null) return -1;
                if (leftTimestamp === null && rightTimestamp !== null) return 1;
                return left.timelineOrder - right.timelineOrder;
            });
        },

        researchHistoryEntryIds() {
            return this.researchHistoryItems.map(entry => entry.id).join('\u001f');
        },

        selectedResearchEntryIndex() {
            if (!this.selectedResearchEntryId) return -1;
            return this.researchHistoryItems.findIndex((entry) => (
                entry.id === this.selectedResearchEntryId
            ));
        },

        currentResearchEntry() {
            if (!this.hasResearchHistory) return null;
            const index = this.selectedResearchEntryIndex;
            return index >= 0 ? this.researchHistoryItems[index] : null;
        },

        currentResearchActivity() {
            return this.currentResearchEntry && this.currentResearchEntry.kind === 'activity'
                ? this.currentResearchEntry.activity
                : null;
        },

        currentSourceReceipt() {
            return this.currentResearchEntry && this.currentResearchEntry.kind === 'source'
                ? this.currentResearchEntry.receipt
                : null;
        },

        currentSourcePayload() {
            return this.currentSourceReceipt
                && this.currentSourceReceipt.ui_payload
                && typeof this.currentSourceReceipt.ui_payload === 'object'
                ? this.currentSourceReceipt.ui_payload
                : {};
        },

        currentSourceCounts() {
            const counts = this.currentSourcePayload && this.currentSourcePayload.counts;
            return counts && typeof counts === 'object' ? counts : {};
        },

        currentSourcePassageCount() {
            return Math.max(0, Number(this.currentSourceCounts.passages) || 0);
        },

        currentSourceStats() {
            const hasCount = key => Object.prototype.hasOwnProperty.call(this.currentSourceCounts, key);
            const stats = [{
                label: 'Pasajes verificados',
                value: this.currentSourcePassageCount,
                tone: this.currentSourcePassageCount ? 'verified' : 'neutral',
            }];
            if (hasCount('sources_matched')) {
                stats.push({
                    label: 'Fuentes disponibles',
                    value: Math.max(0, Number(this.currentSourceCounts.sources_matched) || 0),
                    tone: 'verified',
                });
            }
            return stats;
        },

        currentSourceTitle() {
            const title = String(this.currentSourcePayload.title || 'Fuente consultada').slice(0, 300);
            const matched = Math.max(0, Number(this.currentSourceCounts.sources_matched) || 0);
            return matched > 1 && title.trim().toLocaleLowerCase('es-ES') === 'fuente consultada'
                ? 'Fuentes consultadas'
                : title;
        },

        currentSourceSummary() {
            return String(this.currentSourcePayload.summary || '').slice(0, 1000);
        },

        currentSourceWarnings() {
            return Array.isArray(this.currentSourcePayload.warnings)
                ? this.currentSourcePayload.warnings.filter(item => typeof item === 'string').slice(0, 20)
                : [];
        },

        currentSourceActions() {
            if (!Array.isArray(this.currentSourcePayload.actions)) return [];
            const canOpenPassages = Boolean(
                this.currentSourceReceipt
                && this.currentSourceReceipt.result_ref
                && this.currentSourceReceipt.result_ref.type === 'anchor_set'
                && this.currentSourceReceipt.result_ref.id
            );
            return this.currentSourcePayload.actions
                .filter(item => typeof item === 'string')
                .filter(item => (
                    this.normalizeSourceAction(item) !== 'abrir pasajes' || canOpenPassages
                ))
                .slice(0, 10);
        },

        currentSourceStatusLabel() {
            const labels = {
                running: 'En curso',
                completed: 'Completado',
                partial: 'Con advertencias',
                failed: 'No completado',
                cancelled: 'Detenido',
            };
            if (
                String(this.currentSourceReceipt && this.currentSourceReceipt.status || '') === 'partial'
                && this.currentSourcePassageCount === 0
            ) return 'Sin coincidencias';
            return labels[String(this.currentSourceReceipt && this.currentSourceReceipt.status || '')] || 'En curso';
        },

        currentSourceToneClass() {
            const status = String(this.currentSourceReceipt && this.currentSourceReceipt.status || 'running');
            if (status === 'partial' && this.currentSourcePassageCount === 0) {
                return 'research-activity-display--source-empty';
            }
            return `research-activity-display--source-${status}`;
        },

        processedCurrentResearchActivity() {
            const activityMessage = this.slowToolProgressMessage
                && !this.showDelayedResponseNotice
                && this.message
                && this.isSlowToolProgressRunActive
                && !(this.message.text && this.message.text.length > 0)
                && this.followLatest
                && this.slowToolProgressKey === this.currentSlowToolProgressKey
                ? this.slowToolProgressMessage
                : this.currentResearchActivity && this.currentResearchActivity.message;
            if (!activityMessage) {
                return '';
            }
            try {
                return sanitizeRichHtml(
                    marked.parse(activityMessage),
                    activityMessage,
                );
            } catch (error) {
                return escapePlaintextHtml(activityMessage);
            }
        },

        currentSlowToolProgressKey() {
            if (!this.currentResearchEntry || this.currentResearchEntry.kind !== 'activity') return '';
            return this.buildSlowToolProgressKey(this.currentResearchEntry.id);
        },

        isSlowToolProgressRunActive() {
            return !!(
                this.message
                && (
                    this.message.isLoading === true
                    || (
                        this.message.waitingForUser === true
                        && this.message.resumingContext === true
                    )
                )
            );
        },

        canNavigatePrevious() {
            return this.hasResearchHistory && this.selectedResearchEntryIndex > 0;
        },

        canNavigateNext() {
            return this.hasResearchHistory
                && this.selectedResearchEntryIndex >= 0
                && this.selectedResearchEntryIndex < this.researchHistoryItems.length - 1;
        },

        researchHistoryCount() {
            return this.researchHistoryItems.length;
        },

        researchHistoryPanelId() {
            return `research-history-${String(this._uid || 'message').replace(/[^a-zA-Z0-9_-]/g, '-')}`;
        },
        // Reuse cancellation when a partially written response stops progressing.
        canStopNow() {
            if (!this.message || !this.message.isLoading) return false;
            if (this.message.text && this.message.text.length > 0) return this.showDelayedResponseNotice;
            return true;
        },
        replyPreview() {
            if (!this.collaborative) return null;
            const reply = this.message?.reply_to || this.message?.replyTo;
            if (!reply || typeof reply !== 'object') return null;
            const snippet = String(reply.snippet || '').trim();
            if (!snippet) return null;
            return {
                message_id: String(reply.message_id || '').trim(),
                sender_type: String(reply.sender_type || reply.sender || '').trim(),
                sender_label: String(reply.sender_label || '').trim() || 'Mensaje citado',
                snippet,
                timestamp: String(reply.timestamp || '').trim(),
            };
        },
        canReply() {
            return this.collaborative && !this.message?.isLoading && !this.message?.waitingForUser && !this.is_call_log;
        },
        messageDateTime() {
            if (!this.collaborative || this.message?.isLoading) return "";
            return formatMessageDateTime(this.message);
        }
    },
    methods: {
        replyToMessage() {
            if (!this.canReply) return;
            this.$emit('replyToMessage', this.message);
        },
        ensureSlowToolProgressController() {
            if (this.slowToolProgressController) return this.slowToolProgressController;
            this.slowToolProgressController = createSlowToolProgressController({
                onUpdate: (message, metadata = {}) => {
                    this.slowToolProgressMessage = String(message || '');
                    this.slowToolProgressKey = this.slowToolProgressMessage
                        ? String(metadata.key || '')
                        : '';
                },
            });
            return this.slowToolProgressController;
        },
        stopSlowToolProgress() {
            if (this.slowToolProgressController) {
                this.slowToolProgressController.stop();
            } else {
                this.slowToolProgressMessage = '';
                this.slowToolProgressKey = '';
            }
        },
        buildSlowToolProgressKey(entryId) {
            const expected = this.expectedResearchIdentity();
            return [
                String(window.chat_uuid || '').trim(),
                String(expected.continuationJobId || ''),
                String(expected.responseMessageId || ''),
                String(expected.runId || ''),
                String(entryId || ''),
            ].join('|');
        },
        syncSlowToolProgress() {
            const controller = this.ensureSlowToolProgressController();
            if (
                !this.message
                || !this.isSlowToolProgressRunActive
                || (this.message.text && this.message.text.length > 0)
                || !this.isLatestBotMessage()
                || !this.followLatest
                || !this.currentResearchEntry
                || this.currentResearchEntry.kind !== 'activity'
            ) {
                controller.stop();
                return false;
            }

            const activity = this.currentResearchEntry.activity;
            if (!activity || !this.researchActivityMatchesCurrentRun(activity)) {
                controller.stop();
                return false;
            }

            const entryId = this.currentResearchEntry.id;
            const progressKey = this.buildSlowToolProgressKey(entryId);
            if (controller.activeKey() === progressKey) return true;

            const activityTimestamp = this.researchEntryTimestamp(
                activity.created_at || activity.timestamp
            );
            const started = controller.start({
                anchorMessage: activity.message,
                startedAt: activityTimestamp,
                key: progressKey,
                isActive: () => !!(
                    this.message
                    && this.isSlowToolProgressRunActive
                    && !(this.message.text && this.message.text.length > 0)
                    && this.isLatestBotMessage()
                    && this.followLatest
                    && this.currentResearchEntry
                    && this.currentResearchEntry.kind === 'activity'
                    && this.currentResearchEntry.id === entryId
                    && this.buildSlowToolProgressKey(entryId) === progressKey
                    && this.researchActivityMatchesCurrentRun(activity)
                ),
            });
            return started;
        },
        navigateToReply() {
            if (!this.replyPreview) return;
            this.$emit('navigateToMessage', this.replyPreview);
        },
        htmlEscape(text) {
            if (text === undefined || text === null) {
                return '';
            }
            return text.toString().replace(/[&<>"']/g, (ch) => {
                const map = {
                    '&': '&amp;',
                    '<': '&lt;',
                    '>': '&gt;',
                    '"': '&quot;',
                    "'": '&#39;'
                };
                return map[ch] || ch;
            });
        },
        sanitizeFileName(baseName, extension) {
            const fallback = 'Documento LEXIUS';
            const cleanedBase = (baseName || '')
                .toString()
                .replace(/[\\/:*?"<>|]/g, '')
                .replace(/\s+/g, ' ')
                .trim() || fallback;
            const cleanExtension = extension ? extension.replace('.', '') : '';
            return cleanExtension ? `${cleanedBase}.${cleanExtension}` : cleanedBase;
        },
        stripLexiusDocFilenameHint(text) {
            if (!text || typeof text !== 'string') {
                return text;
            }
            return text.replace(/^[\t ]*Nombre\s+de\s+archivo(?:\s+sugerido)?\s*:[^\n]*\n?/gim, '').replace(/\n{3,}/g, '\n\n');
        },
        stripLexiusReferenceMetadataForDownload(markdown) {
            if (!markdown || typeof markdown !== 'string') {
                return markdown;
            }
            const blockStart = '<<<LEXIUS_REFERENCE_MAP_START>>>';
            const blockEnd = '<<<LEXIUS_REFERENCE_MAP_END>>>';
            const citationHandlePattern = /\[(?:R|D)\s*-?\s*0*\d+(?:\s*[,;]\s*(?:R|D)\s*-?\s*0*\d+)*\](?!\s*\()/i;
            if (!markdown.includes(blockStart)
                && !markdown.includes(blockEnd)
                && !citationHandlePattern.test(markdown)) {
                return markdown;
            }
            const escapePattern = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const completeBlock = new RegExp(
                `[ \\t]*${escapePattern(blockStart)}[\\s\\S]*?${escapePattern(blockEnd)}[ \\t]*(?:\\r?\\n|$)`,
                'g',
            );
            let cleaned = markdown.replace(completeBlock, '');
            [blockStart, blockEnd].forEach((sentinel) => {
                const sentinelLine = new RegExp(
                    `^[^\\n]*${escapePattern(sentinel)}[^\\n]*(?:\\n|$)`,
                    'gm',
                );
                cleaned = cleaned.replace(sentinelLine, '').split(sentinel).join('');
            });
            // Reference maps are not the only internal citation representation.
            // A streamed/legacy draft can contain unresolved R#/D# handles or
            // Markdown reference definitions without carrying a sentinel map.
            cleaned = cleaned
                .replace(/^[\t ]*\[(?:R|D)\s*-?\s*0*\d+\]\s*:\s*[^\n]*(?:\r?\n|$)/gim, '')
                .replace(/\[(?:R|D)\s*-?\s*0*\d+(?:\s*[,;]\s*(?:R|D)\s*-?\s*0*\d+)*\](?!\s*\()/gi, '');
            return `${cleaned
                .replace(/[ \t]+\n/g, '\n')
                .replace(/\n{3,}/g, '\n\n')
                .trim()}${cleaned.trim() ? '\n' : ''}`;
        },
        renderLexiusDocMarkdownForDownload(markdown, fallbackHtml = '') {
            const cleaned = this.stripLexiusReferenceMetadataForDownload(markdown);
            if (cleaned === markdown) {
                return fallbackHtml;
            }
            try {
                const processed = typeof processMarkdownContent === 'function'
                    ? processMarkdownContent(cleaned).trim()
                    : cleaned.trim();
                let html = this.htmlEscape(processed).replace(/\n/g, '<br>');
                if (typeof marked !== 'undefined' && typeof marked.parse === 'function') {
                    html = marked.parse(processed);
                }
                if (typeof window !== 'undefined' && typeof window.sanitizeHTML === 'function') {
                    html = window.sanitizeHTML(html);
                }
                return html;
            } catch (error) {
                return this.htmlEscape(cleaned).replace(/\n/g, '<br>');
            }
        },
        viewportAnchorHash(seed) {
            const text = String(seed || '');
            let hash = 0;
            for (let index = 0; index < text.length; index += 1) {
                hash = ((hash << 5) - hash) + text.charCodeAt(index);
                hash |= 0;
            }
            return Math.abs(hash).toString(36);
        },
        addResponseViewportAnchors(html) {
            if (!html || typeof document === 'undefined') return html;
            try {
                const template = document.createElement('template');
                template.innerHTML = html;
                const occurrences = new Map();
                Array.from(template.content.children).forEach((element) => {
                    const text = String(element.textContent || '').replace(/\s+/g, ' ').trim();
                    if (!text) return;
                    const hash = this.viewportAnchorHash(text.slice(0, 180));
                    const occurrence = Number(occurrences.get(hash) || 0);
                    occurrences.set(hash, occurrence + 1);
                    element.setAttribute(
                        'data-message-viewport-ref',
                        `response:${hash}:${occurrence}`,
                    );
                });
                return template.innerHTML;
            } catch (e) {
                return html;
            }
        },
        markRenderedResponseViewportAnchors() {
            if (!this.$el) return;
            try {
                this.$el.querySelectorAll('md-block.bot-response').forEach((block) => {
                    const occurrences = new Map();
                    Array.from(block.children).forEach((element) => {
                        const text = String(element.textContent || '').replace(/\s+/g, ' ').trim();
                        if (!text) return;
                        const hash = this.viewportAnchorHash(text.slice(0, 180));
                        const occurrence = Number(occurrences.get(hash) || 0);
                        occurrences.set(hash, occurrence + 1);
                        element.setAttribute(
                            'data-message-viewport-ref',
                            `response:${hash}:${occurrence}`,
                        );
                    });
                });
            } catch (e) { }
        },
        viewportMessageIdentity() {
            return String(
                this.message
                && (this.message.client_message_id || this.message.message_id || this.message.response_message_id)
                || ''
            ).trim();
        },
        getVisibleBotResponseText() {
            if (!this.$el) {
                return '';
            }

            const renderedBlocks = this.$el.querySelectorAll('md-block.bot-response');
            for (const block of renderedBlocks) {
                const visibleText = (block.textContent || '').replace(/\s+/g, ' ').trim();
                if (visibleText) {
                    return visibleText;
                }
            }

            return '';
        },
        maybeHandleFirstVisibleAnswerText() {
            if (this.suppressInitialResponseDetection) {
                return;
            }

            if (this.hasReceivedInitialResponse) {
                return;
            }

            const rawText = this.stripLexiusDocFilenameHint(
                this.message && typeof this.message.text === 'string' ? this.message.text : ''
            );
            if (!rawText || !rawText.trim()) {
                return;
            }

            this.$nextTick(() => {
                if (this.suppressInitialResponseDetection) {
                    return;
                }

                if (this.hasReceivedInitialResponse) {
                    return;
                }

                const visibleText = this.getVisibleBotResponseText();
                if (!visibleText) {
                    return;
                }

                this.hasReceivedInitialResponse = true;
            });
        },
        stripFileExtension(filename) {
            if (!filename) return filename;
            return filename.replace(/\.[a-zA-Z0-9]{1,6}$/i, '');
        },
        getSuggestedFileName() {
            if (!this.message || !this.message.text) {
                return null;
            }

            const pattern = /nombre\s+de\s+archivo(?:\s+sugerido)?\s*:\s*(.+)/i;
            const match = this.message.text.match(pattern);
            if (!match) {
                return null;
            }

            let name = match[1] || '';
            name = name.split('\n')[0];
            name = name
                .replace(/[`*><#]/g, '')
                .replace(/_/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
            return name || null;
        },
        getLexiusDocExportPayload(targetIndex = null, { forDownload = false } = {}) {
            if (!this.lexiusDocBlocks || this.lexiusDocBlocks.length === 0) {
                return null;
            }

            let selectedBlocks;
            if (targetIndex === null || targetIndex === undefined) {
                selectedBlocks = this.lexiusDocBlocks;
            } else {
                const numericIndex = parseInt(targetIndex, 10);
                if (Number.isNaN(numericIndex) || numericIndex < 0 || numericIndex >= this.lexiusDocBlocks.length) {
                    return null;
                }
                selectedBlocks = [this.lexiusDocBlocks[numericIndex]];
            }

            const sections = selectedBlocks.map((block, index) => {
                const globalIndex = targetIndex === null || targetIndex === undefined ? index : parseInt(targetIndex, 10);
                const displayIndex = targetIndex === null || targetIndex === undefined ? index + 1 : globalIndex + 1;
                const title = block.title || `Documento ${displayIndex}`;
                const escapedTitle = this.htmlEscape(title);
                const rawMarkdown = block.raw || block.rawWithoutTitle || '';
                const blockHtml = forDownload
                    ? this.renderLexiusDocMarkdownForDownload(
                        rawMarkdown,
                        block.html || block.htmlWithoutTitle || '',
                    )
                    : (block.html || block.htmlWithoutTitle || '');
                return `<section class="lexiusdoc-download-section">${blockHtml}</section>`;
            }).join('<hr />');

            const suggestedName = this.stripFileExtension(this.getSuggestedFileName());
            const primaryBlock = selectedBlocks[0];
            const primaryTitle = (primaryBlock && primaryBlock.title) ? primaryBlock.title : 'Documento LEXIUS';

            let filenameBase = suggestedName || primaryTitle;
            if (suggestedName && targetIndex !== null && targetIndex !== undefined && this.lexiusDocBlocks.length > 1) {
                const numericIndex = parseInt(targetIndex, 10);
                filenameBase = `${suggestedName}_${numericIndex + 1}`;
            }

            const title = filenameBase || primaryTitle;
            const filename = filenameBase || primaryTitle;

            return {
                html: `<div class="lexiusdoc-download-wrapper">${sections}</div>`,
                title,
                filename,
                rawMarkdown: selectedBlocks.map(block => (block.raw || block.rawWithoutTitle || '')).join('\n\n')
            };
        },
        getLexiusDocPlainText(targetIndex = null) {
            if (!this.lexiusDocBlocks || this.lexiusDocBlocks.length === 0) {
                return '';
            }

            if (targetIndex === null || targetIndex === undefined) {
                return this.lexiusDocBlocks.map(block => ((block.raw || block.rawWithoutTitle || '')).trim()).join('\n\n');
            }

            const numericIndex = parseInt(targetIndex, 10);
            if (Number.isNaN(numericIndex) || numericIndex < 0 || numericIndex >= this.lexiusDocBlocks.length) {
                return '';
            }

            const targetBlock = this.lexiusDocBlocks[numericIndex];
            return ((targetBlock.raw || targetBlock.rawWithoutTitle || '')).trim();
        },
        prepareHtmlForExport(html) {
            if (!html || typeof html !== 'string') {
                return html;
            }
            if (typeof convertLinksToAbsoluteOpenPath === 'function') {
                return convertLinksToAbsoluteOpenPath(html);
            }
            return html;
        },
        ensureResearchActivityHistory() {
            // Initialize research activity history if it doesn't exist
            if (!this.message.researchActivityHistory) {
                this.message.researchActivityHistory = [];
            }
        },
        isLatestBotMessage() {
            // Get all messages from parent component
            // Check if parent components exist first
            if (!this.$parent || !this.$parent.$parent || !this.$parent.$parent.currentSession) {
                return false;
            }

            const messages = this.$parent.$parent.currentSession.data;
            if (!messages || messages.length === 0) return false;

            // Find all bot messages
            const botMessages = messages.filter(msg => msg.sender === 'bot');
            if (botMessages.length === 0) return false;

            // Check if this message is the last bot message
            const lastBotMessage = botMessages[botMessages.length - 1];
            return this.message === lastBotMessage;
        },
        terminalActionContext() {
            try {
                const messages = this.$parent && this.$parent.session
                    && Array.isArray(this.$parent.session.data)
                    ? this.$parent.session.data
                    : [];
                const actionState = resolveTerminalActionState(messages, false);
                if (
                    actionState.mode !== 'inline'
                    || messages[actionState.terminalIndex] !== this.message
                ) return null;
                return {
                    state: actionState,
                    trigger: messages[actionState.triggerIndex] || null,
                };
            } catch (e) {
                return null;
            }
        },
        ownsTerminalActions() {
            return !!this.terminalActionContext();
        },
        terminalTriggerIsRunningQueuedAi() {
            const context = this.terminalActionContext();
            const trigger = context && context.trigger;
            return !!(
                trigger
                && trigger.ai_queued === true
                && String(trigger.ai_queue_status || '').trim().toLowerCase() === 'running'
            );
        },
        isSummaryComplete() {
            // Get all messages from parent component
            const messages = this.$parent.session.data;
            // Find the last bot message
            const lastBotMessage = [...messages].reverse().find(msg => msg.sender === 'bot');

            // If this is not the latest bot message, always return true
            if (lastBotMessage && this.message !== lastBotMessage) {
                return true;
            }

            // Otherwise, check if we're currently replying
            return !this.replying;
        },

        // Research activity history methods
        researchEntryIdentityPart(value) {
            return encodeURIComponent(String(value || ''));
        },
        researchActivityFallbackId(activity) {
            const item = activity && typeof activity === 'object' ? activity : {};
            return [
                item.continuation_job_id || item.run_id || '',
                item.response_message_id || '',
                item.created_at || item.timestamp || '',
                item.status || '',
                item.message || '',
                item.query || '',
            ].map(value => this.researchEntryIdentityPart(value)).join(':');
        },
        researchActivityEntryId(activity) {
            const item = activity && typeof activity === 'object' ? activity : {};
            const activitySeq = this.researchActivitySeq(item);
            if (activitySeq) {
                return [
                    'activity',
                    this.researchEntryIdentityPart(item.continuation_job_id || item.run_id || ''),
                    this.researchEntryIdentityPart(item.response_message_id || ''),
                    String(activitySeq),
                ].join(':');
            }
            const explicitId = String(item.id || '').trim();
            if (explicitId) return `activity:id:${this.researchEntryIdentityPart(explicitId)}`;
            return `activity:fallback:${this.researchActivityFallbackId(item)}`;
        },
        researchSourceEntryId(receipt) {
            const toolRunId = String(receipt && receipt.tool_run_id || '').trim();
            if (toolRunId) return `source:${this.researchEntryIdentityPart(toolRunId)}`;
            return `source:fallback:${this.researchEntryIdentityPart(receipt && receipt.created_at || '')}`;
        },
        normalizeResearchActivityForHistory(activity) {
            return {
                ...(activity || {}),
                // A missing server timestamp must not become a new identity
                // on every recovery. researchActivityEntryId derives a
                // deterministic fallback when the server did not provide id
                // or activity_seq.
                timestamp: activity && (activity.created_at || activity.timestamp) || 0,
            };
        },
        selectLatestResearchEntry() {
            const latest = this.researchHistoryItems[this.researchHistoryItems.length - 1] || null;
            if (!latest) {
                this.selectedResearchEntryId = '';
                return false;
            }
            this.selectedResearchEntryId = latest.id;
            this.followLatest = true;
            return true;
        },
        selectResearchEntry(entryId) {
            const index = this.researchHistoryItems.findIndex((entry) => entry.id === entryId);
            if (index < 0) return false;
            this.selectedResearchEntryId = this.researchHistoryItems[index].id;
            this.followLatest = index === this.researchHistoryItems.length - 1;
            return true;
        },
        reconcileResearchSelection() {
            const entries = this.researchHistoryItems;
            if (!entries.length) {
                this.selectedResearchEntryId = '';
                return false;
            }
            if (this.followLatest) {
                return this.selectLatestResearchEntry();
            }
            if (entries.some(entry => entry.id === this.selectedResearchEntryId)) return true;

            // The selected entry was removed (for example, a source read with
            // no matches). Keep the reader on an existing stable entry instead
            // of implicitly jumping to the newest activity they did not pick.
            this.selectedResearchEntryId = entries[0].id;
            // Selection was manual when this branch is reached; retaining the
            // remaining entry must not silently opt the reader back into
            // follow-latest when a recovered activity arrives afterward.
            this.followLatest = false;
            return true;
        },
        toggleResearchHistory() {
            this.ensureResearchActivityHistory();
            this.showResearchHistory = !this.showResearchHistory;
            if (this.showResearchHistory && this.hasResearchHistory) {
                this.selectLatestResearchEntry();
            }
        },

        navigateResearchHistory(direction) {
            this.ensureResearchActivityHistory();
            if (!this.hasResearchHistory) return;
            this.reconcileResearchSelection();
            const currentIndex = this.selectedResearchEntryIndex;
            if (direction === 'previous' && currentIndex > 0) {
                this.selectResearchEntry(this.researchHistoryItems[currentIndex - 1].id);
            } else if (direction === 'next' && currentIndex >= 0
                && currentIndex < this.researchHistoryItems.length - 1) {
                this.selectResearchEntry(this.researchHistoryItems[currentIndex + 1].id);
            }
        },

        navigateToResearchEntry(entryId) {
            this.ensureResearchActivityHistory();
            if (!this.hasResearchHistory) return;
            this.selectResearchEntry(entryId);
        },

        researchEntryLabel(entry, index) {
            if (entry && entry.kind === 'source') {
                const payload = entry.receipt && entry.receipt.ui_payload;
                return String(payload && payload.title || 'Fuente consultada');
            }
            const activity = entry && entry.activity;
            return `Actividad ${index + 1}: ${this.translateStatus(activity && activity.status)}`;
        },

        normalizeSourceAction(value) {
            return String(value || '')
                .trim()
                .toLocaleLowerCase('es-ES')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '');
        },

        displaySourceAction(action) {
            return this.normalizeSourceAction(action) === 'abrir pasajes'
                ? 'Revisar pasajes'
                : String(action || '');
        },

        researchEntryTimestamp(value) {
            if (typeof value === 'number') return Number.isFinite(value) ? value : null;
            const rawValue = String(value || '').trim();
            if (!rawValue) return null;
            const timestampValue = /(?:z|[+-]\d{2}:?\d{2})$/i.test(rawValue)
                ? rawValue
                : `${rawValue}Z`;
            const parsed = Date.parse(timestampValue);
            return Number.isNaN(parsed) ? null : parsed;
        },

        emitSourceReceiptAction(action) {
            if (!this.currentSourceReceipt) return;
            this.$emit('toolAction', {
                action: String(action || ''),
                receipt: this.currentSourceReceipt,
                artifactVersion: null,
            });
        },

        addResearchActivityToHistory(activity) {
            // Initialize research activity history if it doesn't exist
            this.ensureResearchActivityHistory();
            this.message.researchActivityHistory.push(
                this.normalizeResearchActivityForHistory(activity)
            );
            this.reconcileResearchSelection();

            // Save to localStorage - emit event to parent instead of direct store call
            this.$emit('messageUpdated', this.message);

            // Trigger real-time update notification
            this.$emit('researchActivityAdded', this.message);

            // Trigger visual notification for new activity
            this.triggerNewActivityAnimation();
        },
        researchActivityDedupeKey(activity) {
            if (!activity || typeof activity !== 'object') return '';
            const activitySeq = Number(activity.activity_seq);
            if (Number.isInteger(activitySeq) && activitySeq > 0) {
                return [
                    String(activity.continuation_job_id || activity.run_id || ''),
                    String(activity.response_message_id || ''),
                    String(activitySeq),
                ].join('|');
            }
            return [
                String(activity.status || ''),
                String(activity.message || ''),
                String(activity.query || '')
            ].join('|');
        },
        expectedResearchIdentity() {
            const message = this.message || {};
            const continuationJobId = String(message.continuation_job_id || '').trim();
            const responseMessageId = String(
                message.response_message_id || message.message_id || ''
            ).trim();
            return {
                continuationJobId,
                responseMessageId,
                runId: continuationJobId || this.normalizeRunId(message.run_id) || this.activeRunId || '',
            };
        },
        researchActivityMatchesCurrentRun(activity) {
            if (!activity || typeof activity !== 'object') return false;
            const expected = this.expectedResearchIdentity();
            const incomingChatUuid = String(activity.chat_uuid || '').trim();
            const currentChatUuid = String(window.chat_uuid || '').trim();
            if (incomingChatUuid && currentChatUuid && incomingChatUuid !== currentChatUuid) return false;

            const incomingContinuationId = String(activity.continuation_job_id || '').trim();
            const incomingResponseId = String(activity.response_message_id || '').trim();
            const incomingRunId = this.normalizeRunId(activity.run_id);
            if (expected.continuationJobId) {
                // Continuations always have durable identities. A runless
                // activity cannot safely be assigned to whichever paused
                // bubble happens to be latest when the event arrives.
                if (incomingContinuationId !== expected.continuationJobId) return false;
                if (expected.responseMessageId && incomingResponseId !== expected.responseMessageId) return false;
                if (incomingRunId && incomingRunId !== expected.continuationJobId) return false;
                return true;
            }
            if (incomingContinuationId) return false;
            if (incomingResponseId && expected.responseMessageId
                && incomingResponseId !== expected.responseMessageId) return false;
            if (isStaleInFlightChatUpdate(this.staleInFlightChatUpdateArgs(activity))) return false;
            if (incomingRunId && expected.runId && incomingRunId !== expected.runId) return false;
            return true;
        },
        researchActivitySeq(activity) {
            const seq = Number(activity && activity.activity_seq);
            return Number.isInteger(seq) && seq > 0 ? seq : 0;
        },
        async recoverResearchActivityGap(fromActivitySeq = 0, toActivitySeq = 0) {
            const expected = this.expectedResearchIdentity();
            if ((!expected.continuationJobId && !expected.runId) || this.researchGapRecoveryInFlight) return;
            this.researchGapRecoveryInFlight = true;
            try {
                let response;
                if (expected.continuationJobId) {
                    const query = new URLSearchParams({ chat_uuid: String(window.chat_uuid || '') });
                    if (Number(fromActivitySeq) > 0) {
                        query.set('from_activity_seq', String(Number(fromActivitySeq)));
                    }
                    if (Number(toActivitySeq) >= Number(fromActivitySeq) && Number(toActivitySeq) > 0) {
                        query.set('to_activity_seq', String(Number(toActivitySeq)));
                    }
                    response = await fetch(
                        `/chat/continuations/${encodeURIComponent(expected.continuationJobId)}?${query.toString()}`
                    );
                } else {
                    response = await fetch('/check_chat_status', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ uuid: window.chat_uuid }),
                    });
                }
                if (!response.ok) return;
                const projection = await response.json().catch(() => null);
                if (projection && Array.isArray(projection.reasoning_activities)) {
                    this.mergeResearchActivitiesFromServer(projection.reasoning_activities);
                }
            } catch (error) {
                this.logAiFallbackTest('reasoning-gap-recovery-error', this.buildChatFallbackLogDetails(null, {
                    continuation_job_id: expected.continuationJobId,
                    error,
                }));
            } finally {
                this.researchGapRecoveryInFlight = false;
            }
        },
        mergeResearchActivitiesFromServer(activities) {
            if (!Array.isArray(activities) || activities.length === 0) return false;
            this.ensureResearchActivityHistory();
            const existingKeys = new Set(
                this.message.researchActivityHistory
                    .map((activity) => this.researchActivityDedupeKey(activity))
                    .filter(Boolean)
            );
            let added = false;
            [...activities].sort((left, right) => (
                this.researchActivitySeq(left) - this.researchActivitySeq(right)
            )).forEach((activity) => {
                if (!activity || !activity.message || !this.researchActivityMatchesCurrentRun(activity)) return;
                const key = this.researchActivityDedupeKey(activity);
                if (!key || existingKeys.has(key)) return;
                existingKeys.add(key);
                const activitySeq = this.researchActivitySeq(activity);
                if (activitySeq) this.lastResearchActivitySeq = Math.max(this.lastResearchActivitySeq, activitySeq);
                this.message.researchActivityHistory.push(
                    this.normalizeResearchActivityForHistory(activity)
                );
                added = true;
            });
            if (!added) return false;
            this.message.researchActivityHistory.sort((left, right) => {
                const leftSeq = this.researchActivitySeq(left);
                const rightSeq = this.researchActivitySeq(right);
                if (!leftSeq && !rightSeq) return 0;
                if (!leftSeq) return 1;
                if (!rightSeq) return -1;
                return leftSeq - rightSeq;
            });
            this.reconcileResearchSelection();
            this.$emit('messageUpdated', this.message);
            this.$emit('researchActivityAdded', this.message);
            this.triggerNewActivityAnimation();
            return true;
        },
        hasDeliveredAnswerLocally() {
            const rawText = this.stripLexiusDocFilenameHint(
                this.message && typeof this.message.text === 'string' ? this.message.text : ''
            );
            // Partial streamed text is not a delivered answer. A correlated
            // terminal failure must be allowed to replace it; only a true
            // completed (non-cancelled/non-failed) bubble suppresses a late
            // recoverable error for the same channel.
            return !!(
                rawText
                && rawText.trim()
                && this.message
                && this.message.isLoading === false
                && this.message.cancelled !== true
                && this.message.waitingForUser !== true
                && this.message.failed !== true
            );
        },
        isRecoverablePollingErrorMessage(message) {
            const text = String(message || '').toLowerCase();
            return (
                text.includes('tardó demasiado')
                || text.includes('recuperar la respuesta')
                || text.includes('respuesta en curso dejó de avanzar')
            );
        },
        shouldIgnoreTerminalErrorUpdate(data) {
            if (!data || !data.error) return false;
            const message = data.error_message || data.message || '';
            return this.hasDeliveredAnswerLocally() && this.isRecoverablePollingErrorMessage(message);
        },

        async copyMessageToClipboard() {
            if (this.copyInProgress) return;
            if (!this.isSummaryComplete()) {
                alert("Por favor, espere a que se complete el mensaje antes de copiarlo.");
                return;
            }

            this.revealArtifactActionWarning();
            this.copyInProgress = true;
            this.copyFeedback = false;
            this.copyError = '';
            const plainText = this.stripLexiusDocFilenameHint(this.message.text);
            try {
                // Get both the HTML and markdown versions
                let html = this.renderedText;

                // Convert relative URLs to absolute in the HTML content
                html = sanitizeRichHtml(convertLinksToAbsoluteOpenPath(html), plainText);

                await writeClipboardContent({
                    clipboard: navigator.clipboard,
                    ClipboardItemClass: window.ClipboardItem,
                    BlobClass: window.Blob,
                    html,
                    plaintext: plainText,
                });
                this.showCopyFeedback();
            } catch (err) {
                console.error('Failed to copy text: ', err);
                this.copyError = 'No se pudo copiar. Inténtalo de nuevo.';
            } finally {
                this.copyInProgress = false;
            }
        },
        showCopyFeedback() {
            this.copyFeedback = true;
            setTimeout(() => {
                this.copyFeedback = false;
            }, 1000);
        },
        async downloadMessageAsPDF() {
            if (!this.isSummaryComplete()) {
                alert("Por favor, espere a que se complete el mensaje antes de descargarlo.");
                return;
            }

            this.revealArtifactActionWarning();
            try {
                // Show loading state
                this.downloadFeedback = true;
                const preparedHtml = this.prepareHtmlForExport(this.renderedText);
                const requestTitle = `LEXIUS ${window.capitalized_country}`;

                // Call the API endpoint
                const response = await fetch('/convert-html-to-pdf', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        html: preparedHtml,
                        title: requestTitle
                    })
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        handleSessionExpired("el Abogado Virtual");
                    } else {
                        alert("Error al generar el PDF. Por favor, revise su conexión a internet e intente nuevamente.");
                    }
                    console.error(response);
                    return;
                }
                // Get the blob from the response
                const blob = await response.blob();

                // Create download link
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `Abogado Virtual LEXIUS ${window.capitalized_country}.pdf`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

            } catch (error) {
                console.error('Error generating PDF:', error);
                alert('Error al generar el PDF. Por favor, intente nuevamente.');
            } finally {
                // Hide loading state after a short delay
                setTimeout(() => {
                    this.downloadFeedback = false;
                }, 1000);
            }
        },
        async downloadMessageAsDOCX() {
            if (!this.isSummaryComplete()) {
                alert("Por favor, espere a que se complete el mensaje antes de descargarlo.");
                return;
            }

            this.revealArtifactActionWarning();
            try {
                // Show loading state
                this.downloadDocxFeedback = true;
                const preparedHtml = this.prepareHtmlForExport(this.renderedText);
                const requestTitle = `LEXIUS ${window.capitalized_country}`;

                // Call the API endpoint
                const response = await fetch('/convert-html-to-docx', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        html: preparedHtml,
                        title: requestTitle
                    })
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        handleSessionExpired("el Abogado Virtual");
                    } else {
                        alert("Error al generar el documento Word. Por favor, revise su conexión a internet e intente nuevamente.");
                    }
                    console.error(response);
                    return;
                }
                // Get the blob from the response
                const blob = await response.blob();

                // Create download link
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `Abogado Virtual LEXIUS ${window.capitalized_country}.docx`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);

            } catch (error) {
                console.error('Error generating DOCX:', error);
                alert('Error al generar el documento Word. Por favor, intente nuevamente.');
            } finally {
                // Hide loading state after a short delay
                setTimeout(() => {
                    this.downloadDocxFeedback = false;
                }, 1000);
            }
        },
        revealArtifactActionWarning() {
            if (!this.hasDocumentArtifact) return;
            this.artifactActionWarningVisible = true;
        },
        dismissArtifactActionWarning() {
            this.artifactActionWarningVisible = false;
        },
        async downloadLexiusDoc(format, index, button) {
            const targetFormat = format === 'pdf' ? 'pdf' : 'docx';
            const endpoint = targetFormat === 'pdf' ? '/convert-html-to-pdf' : '/convert-html-to-docx';

            const payload = this.getLexiusDocExportPayload(index, { forDownload: true });
            if (!payload) {
                alert('No se encontró el documento para descargar.');
                return;
            }

            const preparedHtml = this.prepareHtmlForExport(payload.html);
            const requestTitle = payload.title || `LEXIUS ${window.capitalized_country}`;
            const downloadFileName = this.sanitizeFileName(payload.filename || payload.title, targetFormat);

            this.setLexiusDocButtonLoading(button, true);

            try {
                const response = await fetch(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        html: preparedHtml,
                        title: requestTitle
                    })
                });

                if (!response.ok) {
                    if (response.status === 401) {
                        handleSessionExpired("el Abogado Virtual");
                    } else {
                        alert('Error al generar la descarga. Por favor, revise su conexión a internet e intente nuevamente.');
                    }
                    console.error(response);
                    return;
                }

                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = downloadFileName || `Documento LEXIUS.${targetFormat}`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
            } catch (error) {
                console.error('Error generating document download:', error);
                alert('Error al generar la descarga. Por favor, intente nuevamente.');
            } finally {
                this.setLexiusDocButtonLoading(button, false);
            }
        },
        setLexiusDocButtonLoading(button, isLoading) {
            if (!button) return;
            const icon = button.querySelector('i');
            if (isLoading) {
                if (icon) {
                    if (!button.dataset.originalIconClass) {
                        button.dataset.originalIconClass = icon.className;
                    }
                    icon.className = 'anticon anticon-loading anticon-spin';
                }
                button.classList.add('is-loading');
            } else {
                if (icon && button.dataset.originalIconClass) {
                    icon.className = button.dataset.originalIconClass;
                }
                button.classList.remove('is-loading');
            }
        },
        setLexiusDocButtonCopySuccess(button) {
            if (!button) return;
            const icon = button.querySelector('i');
            const original = button.dataset.originalIconClass || 'anticon anticon-copy';
            if (icon) {
                icon.className = 'anticon anticon-check';
            }
            button.classList.add('copied');

            setTimeout(() => {
                if (icon) {
                    icon.className = original;
                }
                button.classList.remove('copied');
            }, 1200);
        },
        async copyLexiusDocToClipboard(index, button) {
            const payload = this.getLexiusDocExportPayload(index);
            if (!payload) {
                alert('No se encontró el documento para copiar.');
                return;
            }

            const preparedHtml = this.prepareHtmlForExport(payload.html);
            const plainText = this.getLexiusDocPlainText(index) || payload.rawMarkdown || '';

            try {
                const htmlBlob = new Blob([preparedHtml], { type: 'text/html' });
                const plainTextBlob = new Blob([plainText], { type: 'text/plain' });
                const data = new ClipboardItem({
                    'text/plain': plainTextBlob,
                    'text/html': htmlBlob
                });

                await navigator.clipboard.write([data]);
                this.setLexiusDocButtonCopySuccess(button);
            } catch (error) {
                console.error('Error copying lexiusdoc content:', error);
                alert('No se pudo copiar el documento. Por favor, intente nuevamente.');
            }
        },
        onLexiusDocButtonClick(event) {
            event.preventDefault();
            const button = event.currentTarget || (event.target && event.target.closest('.lexiusdoc-card__action-btn'));
            if (!button || button.classList.contains('is-loading')) {
                return;
            }

            const format = button.getAttribute('data-format');
            const indexAttr = button.getAttribute('data-lexiusdoc-index');
            const index = indexAttr !== null ? parseInt(indexAttr, 10) : null;

            if (!format || Number.isNaN(index)) {
                return;
            }

            if (format === 'copy') {
                this.copyLexiusDocToClipboard(index, button);
            } else {
                this.downloadLexiusDoc(format, index, button);
            }
        },
        attachLexiusDocButtons() {
            if (!this.boundLexiusDocButtonHandler) {
                this.boundLexiusDocButtonHandler = (event) => this.onLexiusDocButtonClick(event);
            }

            const cards = this.$el.querySelectorAll('.lexiusdoc-card');
            cards.forEach(card => card.setAttribute('data-tour', 'chat-response-document'));

            const buttons = this.$el.querySelectorAll('.lexiusdoc-card__action-btn');
            buttons.forEach(button => {
                if (!button.dataset.originalIconClass) {
                    const icon = button.querySelector('i');
                    if (icon) {
                        button.dataset.originalIconClass = icon.className;
                    }
                }
                button.removeEventListener('click', this.boundLexiusDocButtonHandler);
                button.addEventListener('click', this.boundLexiusDocButtonHandler);
            });

        },
        mountResponseAudio() {
            const adapters = window.LexiusResponseAudioAdapters;
            if (!adapters) return;
            const message = this.message || {};
            const messageId = durableChatResponseId(message.response_message_id || message.message_id);
            const chatUuid = this.chatUuid || message.chat_uuid || window.chat_uuid;
            const source = message.response_audio_source || (messageId && chatUuid && !this.is_call_log
                ? { type: 'chat_message', chat_uuid: chatUuid, message_id: messageId, part: 'explanation' }
                : undefined);
            const descriptor = {
                key: `chat:${chatUuid || 'local'}:${messageId || this.responseAudioKey || message.id || message.timestamp || ''}`,
                text: message.text || this.displayText,
                source,
                completed: this.responseAudioComplete,
                anchor: this.$el,
            };
            adapters.mountResponse(this.$refs.responseAudioActions, descriptor);
        },
        // Ensure the lexiusdoc card titles match the filename used for downloads
        updateLexiusDocCardTitles() {
            try {
                const cards = this.$el.querySelectorAll('.lexiusdoc-card');
                if (!cards || cards.length === 0) return;

                cards.forEach(card => {
                    const idxAttr = card.getAttribute('data-lexiusdoc-index');
                    if (idxAttr === null || idxAttr === undefined) return;
                    const idx = parseInt(idxAttr, 10);
                    if (Number.isNaN(idx)) return;

                    const payload = this.getLexiusDocExportPayload(idx);
                    if (!payload) return;

                    const baseName = payload.filename || payload.title;
                    if (!baseName) return;

                    const cleanBase = this.sanitizeFileName(baseName);
                    const titleEl = card.querySelector('.lexiusdoc-card__title');
                    if (titleEl) {
                        titleEl.textContent = cleanBase;
                    }
                });
            } catch (e) {
                console.error('Failed to update lexiusdoc card titles:', e);
            }
        },
        detachLexiusDocButtons() {
            if (this.boundLexiusDocButtonHandler) {
                const buttons = this.$el.querySelectorAll('.lexiusdoc-card__action-btn');
                buttons.forEach(button => {
                    button.removeEventListener('click', this.boundLexiusDocButtonHandler);
                });
            }
        },
        followReferenceFallback(link) {
            if (!link || !link.isConnected) return;
            const destination = String(link.href || link.getAttribute('href') || '').trim();
            if (!destination) return;
            const target = String(link.getAttribute('target') || '').trim();
            if (target && target !== '_self') {
                window.open(destination, target);
                return;
            }
            const documentUrl = window.DocDetailsPopup?.normalizeSupportedUrl(destination);
            if (documentUrl && typeof window.openLink === 'function') {
                window.DocDetailsPopup.hidePopup();
                window.openLink(documentUrl, link);
                return;
            }
            window.location.assign(destination);
        },
        attachLinkHoverEvents() {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }

            const links = this.$el.querySelectorAll('.bot-response a');

            links.forEach(link => {
                if (
                    link.dataset.chatDocDetailsBound === '1'
                    || !window.DocDetailsAPI?.parseReference(link.getAttribute('href'))
                ) {
                    return;
                }
                link.dataset.chatDocDetailsBound = '1';
                link.setAttribute('aria-haspopup', 'dialog');
                link.setAttribute('aria-expanded', 'false');
                let referenceHoverTimeout = null;
                let isReferenceHovered = false;

                link.addEventListener('click', async event => {
                    // Preserve the browser's open-in-new-tab/window and download
                    // behavior. Only an ordinary primary click is enhanced with
                    // the details surface.
                    if (!shouldInterceptReferenceClick(event)) return;
                    event.preventDefault();
                    event.stopPropagation();
                    this.persistentPopupTrigger = link;
                    window.clearTimeout(referenceHoverTimeout);
                    referenceHoverTimeout = null;
                    isReferenceHovered = false;
                    if (this.hoverTimeout) {
                        clearTimeout(this.hoverTimeout);
                        this.hoverTimeout = null;
                    }
                    if (this.hidePopupTimeout) {
                        clearTimeout(this.hidePopupTimeout);
                        this.hidePopupTimeout = null;
                    }
                    let detailsOutcome = null;
                    let detailsRequestVersion = 0;
                    try {
                        const detailsRequest = window.DocDetailsPopup.showDocDetails(link, this.messagesCount, {
                            mode: 'persistent',
                            chatUuid: window.chat_uuid,
                            answerReady: !this.replying,
                        });
                        detailsRequestVersion = Number(window.DocDetailsPopup.requestVersion || 0);
                        detailsOutcome = await detailsRequest;
                    } catch (error) {
                        console.error('Failed to load chat source details:', error);
                    }
                    const requestIsCurrent = detailsOutcome && typeof detailsOutcome.current === 'boolean'
                        ? detailsOutcome.current
                        : !!(
                            detailsRequestVersion
                            && detailsRequestVersion === Number(window.DocDetailsPopup.requestVersion || 0)
                        );
                    const detailsOpened = detailsOutcome && typeof detailsOutcome.opened === 'boolean'
                        ? detailsOutcome.opened
                        : (() => {
                            const popupId = String(link.getAttribute('aria-controls') || '');
                            const popup = popupId ? document.getElementById(popupId) : null;
                            return !!(
                                popup
                                && link.getAttribute('aria-expanded') === 'true'
                                && popup.style.display !== 'none'
                                && popup.childElementCount > 0
                            );
                        })();
                    if (!requestIsCurrent) return;
                    if (
                        !detailsOpened
                        && this.persistentPopupTrigger === link
                        && link.isConnected
                    ) {
                        this.followReferenceFallback(link);
                    }
                });

                if (window.DocDetailsPopup.supportsHover()) {
                    link.addEventListener('mouseenter', () => {
                        isReferenceHovered = true;
                        window.clearTimeout(referenceHoverTimeout);
                        referenceHoverTimeout = window.setTimeout(() => {
                            window.DocDetailsPopup.showDocDetails(link, this.messagesCount, {
                                mode: 'transient',
                                chatUuid: window.chat_uuid,
                                answerReady: !this.replying,
                                shouldOpen: () => isReferenceHovered,
                            });
                        }, 300);
                    });
                    link.addEventListener('mouseleave', () => {
                        isReferenceHovered = false;
                        window.clearTimeout(referenceHoverTimeout);
                        referenceHoverTimeout = null;
                    });
                }
            });
        },
        attachSourcesGroupTriggerEvents() {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }

            if (!this.$el) {
                return;
            }

            const container = this.$el.querySelector('.bot-response');
            if (!container) {
                return;
            }

            const triggers = container.querySelectorAll('.sources-group-trigger');
            triggers.forEach(trigger => {
                if (trigger.dataset.chatSourcesBound === '1') {
                    return;
                }
                // The shared document-details module may already own this
                // trigger. Do not layer another click/hover timer on top of it.
                if (trigger.dataset.sourcesBound === '1') {
                    return;
                }

                trigger.dataset.chatSourcesBound = '1';
                trigger.dataset.sourcesBound = '1';
                trigger.setAttribute('aria-haspopup', 'dialog');
                trigger.setAttribute('aria-expanded', 'false');

                    const openGroup = (event, mode = 'persistent') => {
                    if (event) {
                        event.preventDefault();
                    }
                    if (this.hoverTimeout) {
                        clearTimeout(this.hoverTimeout);
                        this.hoverTimeout = null;
                    }
                    if (mode === 'persistent') {
                        this.persistentPopupTrigger = trigger;
                        if (this.hidePopupTimeout) {
                            clearTimeout(this.hidePopupTimeout);
                            this.hidePopupTimeout = null;
                        }
                    }
                    this.openSourcesGroup(trigger, mode);
                };

                const handleKeyDown = (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        openGroup(event);
                    }
                };

                if (this.isMobileDevice) {
                    trigger.addEventListener('click', openGroup);
                    trigger.addEventListener('keydown', handleKeyDown);
                } else {
                    trigger.addEventListener('click', openGroup);
                    trigger.addEventListener('keydown', handleKeyDown);

                    let isHovered = false;
                    trigger.addEventListener('mouseenter', () => {
                        isHovered = true;
                        if (this.hoverTimeout) {
                            clearTimeout(this.hoverTimeout);
                        }
                        this.hoverTimeout = setTimeout(() => {
                            this.openSourcesGroup(trigger, 'transient', () => isHovered);
                        }, 300);
                    });

                    trigger.addEventListener('mouseleave', () => {
                        isHovered = false;
                        if (this.hoverTimeout) {
                            clearTimeout(this.hoverTimeout);
                            this.hoverTimeout = null;
                        }
                        // DocDetailsPopup owns the trigger-to-popup hover bridge.
                        // Scheduling another close here ignores its popup hover state.
                    });
                }
            });
        },
        async openSourcesGroup(triggerElement, mode = 'persistent', shouldOpen = null) {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }

            if (!triggerElement) {
                return;
            }

            const raw = triggerElement.getAttribute('data-sources-json');
            if (!raw) {
                return;
            }

            let sources;
            try {
                sources = JSON.parse(decodeURIComponent(raw));
            } catch (error) {
                console.error('Invalid sources payload for grouped references:', error);
                return;
            }

            if (!Array.isArray(sources) || sources.length === 0) {
                return;
            }

            this.currentHoveredLink = triggerElement;
            this.isShowingPopup = true;
            window.isShowingDocumentPopup = true;
            if (mode === 'persistent') {
                this.persistentPopupTrigger = triggerElement;
                if (this.hoverTimeout) {
                    clearTimeout(this.hoverTimeout);
                    this.hoverTimeout = null;
                }
                if (this.hidePopupTimeout) {
                    clearTimeout(this.hidePopupTimeout);
                    this.hidePopupTimeout = null;
                }
            }

            try {
                await DocDetailsPopup.showMultipleDocs(triggerElement, sources, {
                    messagesCount: this.messagesCount,
                    chatUuid: window.chat_uuid,
                    answerReady: !this.replying,
                    mode,
                    shouldOpen,
                });
            } catch (error) {
                console.error('Failed to show grouped document details:', error);
            }
        },
        reopenGroupedSources(previousTrigger) {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }

            if (!previousTrigger || !this.$el) {
                return;
            }

            const payload = previousTrigger.getAttribute('data-sources-json');
            if (!payload) {
                return;
            }

            const container = this.$el.querySelector('.bot-response');
            if (!container) {
                return;
            }

            const matchingTrigger = Array.from(container.querySelectorAll('.sources-group-trigger'))
                .find(el => el.getAttribute('data-sources-json') === payload);

            if (matchingTrigger) {
                this.openSourcesGroup(matchingTrigger);
            }
        },
        closePopup() {
            this.hideDocDetailsPopup();
            this.isHoveringPopup = false;
            this.$parent.$parent.popupVisible = false;
            window.isShowingDocumentPopup = false;
            window.current_popup_document_path = null;
            console.log("Cleared popup document path");
        },
        showMobileDetailsPopup(linkElement, htmlDetails) {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }
            window.DocDetailsPopup.showPopup(linkElement, htmlDetails, { mode: 'persistent' });
        },
        savePopUpLinkPath(href) {
            // Store the document path for summary generation
            const url = new URL(href, window.location.origin);
            const docId = url.searchParams.get('id');
            if (docId) {
                fetch(`/get_document_path?id=${encodeURIComponent(docId)}`)
                    .then(resp => resp.json())
                    .then(data => {
                        if (data.path) {
                            window.current_popup_document_path = data.path;
                        }
                    })
                    .catch(err => {
                        console.error("Error fetching document path:", err);
                    });
            } else if (url.pathname.startsWith('/Legislaci')) {
                // Unquote the path
                window.current_popup_document_path = decodeURIComponent(url.pathname);

                // Remove the first /
                if (window.current_popup_document_path.startsWith('/')) {
                    window.current_popup_document_path = window.current_popup_document_path.substring(1);
                }
            }
        },
        addClickOutsideListener(popup) {
            const handleClickOutside = (event) => {
                if (popup && !popup.contains(event.target)) {
                    this.hideDocDetailsPopup();
                    document.removeEventListener('click', handleClickOutside);
                }
            };

            // Small delay to prevent immediate triggering
            setTimeout(() => {
                document.addEventListener('click', handleClickOutside);
            }, 100);
        },
        addScrollListener() {
            const handleScroll = () => {
                this.hideDocDetailsPopup();
                window.removeEventListener('scroll', handleScroll);
            };

            window.addEventListener('scroll', handleScroll);
        },
        showDocDetailsPopup(linkElement, htmlDetails) {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }
            window.DocDetailsPopup.showPopup(linkElement, htmlDetails, {
                mode: window.DocDetailsPopup.supportsHover() ? 'transient' : 'persistent',
            });
        },
        onLinkHover(event) {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                this.hideDocDetailsPopup();
                return;
            }

            const link = event.target;
            this.currentHoveredLink = link;

            // Clear any existing hover timeout
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
            }

            // Set a timeout before showing the popup
            this.hoverTimeout = setTimeout(async () => {
                if (this.isDocDetailsSuppressedForDemoRecording()) {
                    this.hideDocDetailsPopup();
                    return;
                }

                const href = link.getAttribute('href');
                if (!href) return;

                const htmlDetails = await DocDetailsAPI.fetchDetails(
                    href,
                    this.messagesCount,
                    window.chat_uuid,
                    !this.replying
                );

                if (htmlDetails) {
                    this.showDocDetailsPopup(link, htmlDetails);
                }
            }, 300); // 300ms delay before showing popup
        },
        onLinkLeave(trigger = null) {
            // Clear the hover timeout when leaving the link
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }

            // A click (or keyboard activation) intentionally pins the sources
            // dialog. A subsequent mouseleave must not schedule a delayed close.
            if (
                this.persistentPopupTrigger
                && window.DocDetailsPopup?.activeMode === 'persistent'
            ) {
                return;
            }

            // Clear any existing timeout
            if (this.hidePopupTimeout) {
                clearTimeout(this.hidePopupTimeout);
            }

            // Set a new timeout
            this.hidePopupTimeout = setTimeout(() => {
                if (!this.isHoveringPopup) {
                    this.hideDocDetailsPopup();
                }
            }, 500);
        },
        hideDocDetailsPopup() {
            if (window.DocDetailsPopup?.activeMode) {
                window.DocDetailsPopup.hidePopup();
            }
            if (this.hidePopupTimeout) {
                clearTimeout(this.hidePopupTimeout);
                this.hidePopupTimeout = null;
            }
            if (this.hoverTimeout) {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = null;
            }
            this.persistentPopupTrigger = null;

            if (!this.isHoveringPopup) {
                // Update parent state directly
                this.$parent.$parent.popupVisible = false;
                this.$parent.$parent.popupContentText = '';
                this.currentHoveredLink = null;
                window.isShowingDocumentPopup = false;
                this.isShowingPopup = false;
                window.current_popup_document_path = null;
                console.log("Cleared popup document path");
            }
        },
        // Gelper method to get doc details
        async getDocDetails(link) {
            if (this.isDocDetailsSuppressedForDemoRecording()) {
                return null;
            }

            const href = link.getAttribute('href');
            return await DocDetailsAPI.fetchDetails(href, this.messagesCount, window.chat_uuid, !this.replying);
        },
        isDocDetailsSuppressedForDemoRecording() {
            if (typeof window === 'undefined') {
                return false;
            }

            if (window.__lexiusDisableDocDetailsPopup === true) {
                return true;
            }

            if (window.__lexiusDemoRecording) {
                return true;
            }

            if (
                window.DocDetailsPopup
                && typeof window.DocDetailsPopup.isSuppressedForDemoRecording === 'function'
                && window.DocDetailsPopup.isSuppressedForDemoRecording()
            ) {
                return true;
            }

            try {
                const params = new URLSearchParams(window.location.search || '');
                return params.get('demo_recording') === '1';
            } catch (e) {
                return false;
            }
        },
        calculateSafePopupPosition(linkElement, popup) {
            const rect = linkElement.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;
            const popupHeight = popup.offsetHeight;
            const popupWidth = popup.offsetWidth;
            const scrollY = window.scrollY || window.pageYOffset;
            const scrollX = window.scrollX || window.pageXOffset;

            let top, left;

            if (this.isMobileDevice) {
                // For mobile: center the popup within the viewport
                top = scrollY + (viewportHeight - popupHeight) / 2;
                left = scrollX + (viewportWidth - popupWidth) / 2;

                // Ensure the popup doesn't exceed viewport boundaries
                top = Math.max(scrollY + 10, Math.min(top, scrollY + viewportHeight - popupHeight - 10));
                left = Math.max(scrollX + 10, Math.min(left, scrollX + viewportWidth - popupWidth - 10));
            } else {
                // For desktop: position the popup relative to the link element
                const margin = 10;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;
                const spaceRight = viewportWidth - rect.left;
                const spaceLeft = rect.left;

                // Decide whether to show above or below the link
                if (spaceBelow >= popupHeight + margin) {
                    // Enough space below: show below the link
                    top = scrollY + rect.bottom + margin;
                } else if (spaceAbove >= popupHeight + margin) {
                    // Enough space above: show above the link
                    top = scrollY + rect.top - popupHeight - margin;
                } else {
                    // Not enough space above or below: adjust to fit within viewport
                    if (spaceBelow > spaceAbove) {
                        top = scrollY + rect.bottom + margin;
                        if (popupHeight > spaceBelow - margin) {
                            top = scrollY + viewportHeight - popupHeight - margin;
                        }
                    } else {
                        top = scrollY + margin;
                        if (popupHeight > spaceAbove - margin) {
                            top = scrollY + margin;
                        }
                    }
                }

                // Horizontal positioning: align popup's left edge with link's left edge
                left = scrollX + rect.left;

                // Ensure the popup doesn't overflow the right edge
                if (left + popupWidth + margin > scrollX + viewportWidth) {
                    left = scrollX + viewportWidth - popupWidth - margin;
                }

                // Ensure the popup doesn't overflow the left edge
                if (left < scrollX + margin) {
                    left = scrollX + margin;
                }

                // Final vertical bounds check
                if (top + popupHeight + margin > scrollY + viewportHeight) {
                    top = scrollY + viewportHeight - popupHeight - margin;
                }
                if (top < scrollY + margin) {
                    top = scrollY + margin;
                }
            }

            console.log('Popup Position:', { top, left }); // Debugging aid

            return { top, left };
        },
        showMindMap() {
            if (!this.isSummaryComplete()) {
                alert("Por favor, espere a que se complete el mensaje antes de generar el mapa mental.");
                return;
            }

            // Create a temporary element to hold the message content
            const tempElement = document.createElement('div');
            tempElement.setAttribute('mdContent', this.message.text);
            tempElement.id = 'temp-mindmap-content';
            document.body.appendChild(tempElement);

            // Hide the chat modal with the close-chat-modal event
            $(document).trigger('close-chat-modal');

            // Add one-time event listener to reopen chat modal when mindmap modal is closed
            const markmapModal = document.getElementById('markmapModal');
            markmapModal.addEventListener('hidden.bs.modal', () => {
                // Show the chat modal again
                $(document).trigger('open-chat-modal');
            }, { once: true });

            // Call the existing showMindMap function from script.js
            showMindMap('temp-mindmap-content').then(() => {
                // Clean up the temporary element after the mindmap is shown
                document.body.removeChild(tempElement);
            });
        },
        shouldReceiveResearchActivity() {
            return !!(
                this.message
                && (
                    this.message.isLoading === true
                    || (
                        this.message.waitingForUser === true
                        && this.message.resumingContext === true
                    )
                )
            );
        },
        sessionMessages() {
            const parentSession = this.$parent && this.$parent.session;
            if (parentSession && Array.isArray(parentSession.data)) {
                return parentSession.data;
            }
            const layoutSession = this.$parent && this.$parent.$parent && this.$parent.$parent.currentSession;
            if (layoutSession && Array.isArray(layoutSession.data)) {
                return layoutSession.data;
            }
            return [];
        },
        completedChatIdentitiesFromSession() {
            return completedChatIdentitiesFromMessages(this.sessionMessages(), this.message);
        },
        staleInFlightChatUpdateArgs(data = {}) {
            const completed = this.completedChatIdentitiesFromSession();
            return {
                incomingRunId: data && data.run_id,
                reservedRunId: this.message && this.message.run_id,
                lastCompletedRunId: this.lastCompletedRunId || completed.lastCompletedRunId,
                incomingResponseMessageId: data && data.response_message_id,
                reservedResponseMessageId: this.message && (
                    this.message.response_message_id || this.message.message_id
                ),
                completedResponseMessageIds: completed.responseMessageIds,
                completedRunIds: completed.runIds,
                completedMessageTexts: completed.texts,
                incomingText: data && (
                    typeof data.text === 'string'
                        ? data.text
                        : data.message
                ),
            };
        },
        bindReservedChatRunId() {
            const reservedRunId = this.normalizeRunId(this.message && this.message.run_id);
            if (!reservedRunId) return reservedRunId;
            if (!this.activeRunId) {
                this.activeRunId = reservedRunId;
                return reservedRunId;
            }
            if (this.activeRunId === reservedRunId) return reservedRunId;
            const comparison = this.compareRunIds(this.activeRunId, reservedRunId);
            if (comparison > 0) {
                this.activeRunId = reservedRunId;
                this.chatSequence = 0;
                this.chatChunkState = { groups: {}, completedIds: [] };
                this.message.text = '';
                this.message.cancelled = false;
                this.message.waitingForUser = false;
                this.hasReceivedInitialResponse = false;
                this.suppressInitialResponseDetection = false;
                this.resetRealtimeActivityState();
                this.isReasoningDuringChat = false;
                this.removeReasoningIndicator();
            }
            return reservedRunId;
        },
        releaseResearchSubscription() {
            const channel = this.ablyChannel;
            const messageListener = this.researchMessageListener;
            const stateListener = this.researchStateListener;
            if (channel) {
                try {
                    if (messageListener && typeof channel.unsubscribe === 'function') {
                        channel.unsubscribe('update', messageListener);
                    }
                } catch (e) { }
                try {
                    if (stateListener && typeof channel.off === 'function') {
                        channel.off(stateListener);
                    }
                } catch (e) { }
            }
            this.ablyChannel = null;
            this.researchMessageListener = null;
            this.researchStateListener = null;
        },
        subscribeToResearchActivity() {
            // A durable continuation keeps the paused bubble visible while it
            // resumes. It still owns live research updates even though the
            // ordinary loading flag is false during that UI state.
            if (!this.isLatestBotMessage() || !this.shouldReceiveResearchActivity()) {
                console.log(`BotMessage: Not subscribing to research activity - no active reply for this message`);
                return;
            }

            if (this.isAiFallbackTestEnabled()) {
                this.unsubscribeFromResearchActivity();
                this.logAiFallbackTest('research-updates-disabled', this.buildChatFallbackLogDetails(null, {
                    reason: 'test-ai-fallback'
                }));
                return;
            }

            const targetChatUuid = String(window.chat_uuid || '').trim();
            if (!targetChatUuid) return;
            const targetChannelName = `search-activity-${targetChatUuid}`;

            // Avoid duplicate subscriptions while acceptance/reconnection hooks race.
            if (this.ablyChannel && this.ablyChannel.name === targetChannelName) {
                console.log(`BotMessage: Already subscribed to ${targetChannelName}`);
                return;
            }
            const subscriptionAttempt = ++this.researchSubscriptionAttempt;
            this.ablyRetryCount = 0;

            // Require Ably to be available - no polling fallback
            const checkAblyAndSubscribe = async () => {
                if (subscriptionAttempt !== this.researchSubscriptionAttempt) return;
                if (
                    typeof Ably !== 'undefined'
                    && (window.ably_realtime_client || typeof window.getChatAblyClient === 'function')
                ) {
                    if (
                        String(window.chat_uuid || '').trim() === targetChatUuid
                        && this.shouldReceiveResearchActivity()
                        && this.isLatestBotMessage()
                    ) {
                        const channelName = targetChannelName;

                        // If we are already on the right channel, do nothing.
                        if (this.ablyChannel && this.ablyChannel.name === channelName) {
                            console.log(`BotMessage: Already subscribed to ${channelName}`);
                            return;
                        }

                        // Remove only this component's listeners. The channel
                        // object is shared with other consumers on the client.
                        if (this.ablyChannel) {
                            this.releaseResearchSubscription();
                        }

                        console.log(`BotMessage: Subscribing to research activity for chat_uuid: ${window.chat_uuid}`);
                        let ablyClient = null;
                        if (typeof window.getChatAblyClient === 'function') {
                            try {
                                ablyClient = await window.getChatAblyClient(targetChatUuid);
                            } catch (e) {
                                console.warn('BotMessage: Failed to get chat-scoped Ably client for research activity', e);
                            }
                        }
                        ablyClient = ablyClient || window.ably_realtime_client;
                        if (
                            subscriptionAttempt !== this.researchSubscriptionAttempt
                            || String(window.chat_uuid || '').trim() !== targetChatUuid
                            || !this.shouldReceiveResearchActivity()
                            || !this.isLatestBotMessage()
                        ) {
                            return;
                        }
                        if (!ablyClient) {
                            console.warn('BotMessage: Ably client unavailable for research activity');
                            return;
                        }
                        const channel = ablyClient.channels.get(channelName);
                        this.ablyChannel = channel;

                        const stateListener = (stateChange) => {
                            console.log('BotMessage: Research channel state:', stateChange.current, stateChange.reason);
                            if (stateChange.current === 'failed') {
                                console.error('BotMessage: Research channel failed:', stateChange.reason);
                                // Research activity failure is not critical, just log it
                            }
                        };
                        this.researchStateListener = stateListener;
                        this.ablyChannel.on(stateListener);

                        const messageListener = (message) => {
                            // Double-check we're still the latest bot message before processing
                            if (!this.isLatestBotMessage() || !this.shouldReceiveResearchActivity()) {
                                console.log('BotMessage: Ignoring research activity - no active reply for this message');
                                return;
                            }

                            console.log('BotMessage: Received Ably research activity:', message.data);

                            if (!this.researchActivityMatchesCurrentRun(message.data)) {
                                console.log('BotMessage: Ignoring research activity for another run or response');
                                return;
                            }

                            if (message.data && (message.data.status === "searching" || message.data.status === "reasoning")) {
                                this.markChatLiveness('research', {
                                    clearInitialGrace: false,
                                    restartInitialGrace: true,
                                    resetTimeout: true,
                                    stopRecoveryPolling: true,
                                    countsAsChatRealtimeActivity: false,
                                });

                                // Check if we're currently writing a chat response
                                const isChatGenerating = this.message.isLoading && this.message.text.length > 0;

                                // If reasoning is happening during chat generation, mark it
                                if (isChatGenerating && message.data.status === 'reasoning') {
                                    this.isReasoningDuringChat = true;
                                    this.lastReasoningActivityTime = Date.now();
                                    this.showReasoningIndicator();
                                }

                                this.researchActivity = message.data;
                                this.addToResearchHistory(message.data);
                            } else {
                                this.researchActivity = null;
                            }
                        };
                        this.researchMessageListener = messageListener;
                        this.ablyChannel.subscribe('update', messageListener);
                    }
                } else {
                    // If Ably is not ready yet, try again in 100ms (with a limit to avoid infinite loops)
                    console.log("BotMessage: Ably is not ready yet for research activity");
                    // Only retry a few times, then give up gracefully
                    const maxRetries = 50; // 5 seconds total
                    if (!this.ablyRetryCount) this.ablyRetryCount = 0;

                    if (this.ablyRetryCount < maxRetries) {
                        this.ablyRetryCount++;
                        setTimeout(checkAblyAndSubscribe, 100);
                    } else {
                        console.warn("BotMessage: Ably not available for research activity after retries - continuing without research updates");
                    }
                }
            };

            checkAblyAndSubscribe();
        },
        unsubscribeFromResearchActivity() {
            this.researchSubscriptionAttempt++;
            this.releaseResearchSubscription();
            this.researchActivity = null;
            this.stopSlowToolProgress();
        },
        releaseChatSubscription() {
            const channel = this.chatChannel;
            if (channel) {
                try {
                    if (this.chatMessageListener && typeof channel.unsubscribe === 'function') {
                        channel.unsubscribe('chat_update', this.chatMessageListener);
                    }
                } catch (e) { }
                [
                    ['failed', this.chatFailedStateListener],
                    ['suspended', this.chatSuspendedStateListener],
                    ['detached', this.chatDetachedStateListener],
                ].forEach(([eventName, listener]) => {
                    try {
                        if (listener && typeof channel.off === 'function') {
                            channel.off(eventName, listener);
                        }
                    } catch (e) { }
                });
            }
            this.chatChannel = null;
            this.chatMessageListener = null;
            this.chatFailedStateListener = null;
            this.chatSuspendedStateListener = null;
            this.chatDetachedStateListener = null;
        },
        subscribeToChatUpdates() {
            // Only subscribe if this is the latest loading message. Capacity
            // retries are not active chat runs yet, so a new placeholder must
            // not attach until /chat_stream has reserved this turn.
            if (
                !this.message.isLoading
                || !window.chat_uuid
                || !this.isLatestBotMessage()
                || this.message.awaitingInitialRequestAcceptance === true
            ) {
                console.log('BotMessage: Not subscribing to chat updates - not the latest bot message or not loading');
                return;
            }

            if (this.isAiFallbackTestEnabled()) {
                this.chatSubscriptionAttempt++;
                this.releaseChatSubscription();
                this.isReceivingViaAbly = false;
                this.clearChatUpdateTimeout();
                this.clearInitialUpdateGraceTimeout();
                this.logAiFallbackTest('forced-polling-start', this.buildChatFallbackLogDetails(null, {
                    reason: 'test-ai-fallback'
                }));
                this.startChatRecoveryPolling('test-ai-fallback');
                return;
            }

            if (!window.ably_realtime_client && typeof initializeAbly === 'function') {
                try { initializeAbly(); } catch (e) { }
            }

            if (typeof window.getChatAblyClient !== 'function') {
                console.warn('BotMessage: Ably not available - falling back to chat status polling');
                this.startChatRecoveryPolling('no-ably-client');
                return;
            }

            const targetChatUuid = String(window.chat_uuid || '').trim();
            if (!targetChatUuid) return;
            const channelName = `chat-updates-${targetChatUuid}`;

            // If we are already on the right channel, do nothing
            if (this.chatChannel && this.chatChannel.name === channelName) {
                console.log(`BotMessage: Already subscribed to chat updates for uuid: ${window.chat_uuid}`);
                return;
            }

            console.log(`BotMessage: Subscribing to chat updates for uuid: ${window.chat_uuid}`);

            // Release only this message component's listeners. ChatLayout owns
            // the long-lived channel used by tool receipts and continuations.
            if (this.chatChannel) {
                this.releaseChatSubscription();
            }

            const subscriptionAttempt = ++this.chatSubscriptionAttempt;
            window.getChatAblyClient(targetChatUuid).then((client) => {
                if (
                    subscriptionAttempt !== this.chatSubscriptionAttempt
                    || String(window.chat_uuid || '').trim() !== targetChatUuid
                    || !this.message.isLoading
                    || !this.isLatestBotMessage()
                    || this.message.awaitingInitialRequestAcceptance === true
                ) {
                    return;
                }
                if (!client) {
                    this.startChatRecoveryPolling('no-chat-ably-client');
                    return;
                }
                const channel = client.channels.get(channelName);
                this.chatChannel = channel;

                // Capacity retries are not active chat runs yet. Start recovery
                // timers only after the initial request has been accepted.
                if (!this.message.awaitingInitialRequestAcceptance) {
                    this.resetChatUpdateTimeout();
                    this.scheduleInitialUpdateGraceTimeout();
                }

                // Subscribe to 'chat_update' events
                const messageListener = (message) => {
                    // Double-check we're still the latest bot message before processing
                    if (
                        !this.isLatestBotMessage()
                        || !this.message.isLoading
                        || this.message.awaitingInitialRequestAcceptance === true
                    ) {
                        console.log('BotMessage: Ignoring chat update - no longer the latest bot message or not loading');
                        return;
                    }

                    console.log('BotMessage: Received Ably chat update:', message.data);
                    if (message.data) {
                        this.isReceivingViaAbly = true;
                        this.handleChatUpdate(message.data);
                    }
                };
                this.chatMessageListener = messageListener;
                channel.subscribe('chat_update', messageListener);

                // Handle channel state changes for better error handling
                this.chatFailedStateListener = (stateChange) => {
                    console.error('BotMessage: Chat channel failed:', stateChange.reason);
                    this.startChatRecoveryPolling('channel-failed');
                };
                this.chatSuspendedStateListener = (stateChange) => {
                    console.warn('BotMessage: Chat channel suspended:', stateChange.reason);
                    this.startChatRecoveryPolling('channel-suspended');
                };
                this.chatDetachedStateListener = (stateChange) => {
                    if (this.message.isLoading && this.isLatestBotMessage()) {
                        console.warn('BotMessage: Chat channel detached while loading:', stateChange.reason);
                        this.startChatRecoveryPolling('channel-detached');
                    }
                };
                channel.on('failed', this.chatFailedStateListener);
                channel.on('suspended', this.chatSuspendedStateListener);
                channel.on('detached', this.chatDetachedStateListener);
            }).catch((error) => {
                if (subscriptionAttempt !== this.chatSubscriptionAttempt) return;
                console.warn('BotMessage: Chat-scoped Ably unavailable - falling back to status polling', error);
                this.startChatRecoveryPolling('chat-ably-error');
            });
        },
        unsubscribeFromChatUpdates() {
            this.chatSubscriptionAttempt++;
            this.releaseChatSubscription();
            this.clearChatUpdateTimeout();
            this.clearInitialUpdateGraceTimeout();
            this.stopChatRecoveryPolling();
            this.isReceivingViaAbly = false;
        },
        clearInitialUpdateGraceTimeout() {
            if (this.initialUpdateGraceTimeout) {
                clearTimeout(this.initialUpdateGraceTimeout);
                this.initialUpdateGraceTimeout = null;
            }
        },
        bindAcceptedChatResponseId(data = {}) {
            const incomingResponseId = durableChatResponseId(
                data && data.response_message_id
            );
            if (!incomingResponseId || !this.message) return '';
            const reservedResponseId = durableChatResponseId(
                this.message.response_message_id || this.message.message_id
            );
            if (reservedResponseId && reservedResponseId !== incomingResponseId) {
                return '';
            }
            try {
                this.$set(this.message, 'response_message_id', incomingResponseId);
            } catch (e) {
                this.message.response_message_id = incomingResponseId;
            }
            try {
                this.$set(this.message, 'message_id', incomingResponseId);
            } catch (e) {
                this.message.message_id = incomingResponseId;
            }
            return incomingResponseId;
        },
        scheduleInitialUpdateGraceTimeout() {
            this.clearInitialUpdateGraceTimeout();
            this.initialUpdateGraceTimeout = setTimeout(() => {
                if (this.message.isLoading && this.isLatestBotMessage() && !this.hasReceivedInitialResponse) {
                    console.warn('BotMessage: No initial chat text arrived within the latest grace window; starting recovery polling');
                    this.startChatRecoveryPolling('initial-no-liveness');
                }
            }, this.INITIAL_UPDATE_GRACE_MS);
        },
        restartInitialUpdateGraceTimeout() {
            if (!this.message.isLoading || !this.isLatestBotMessage() || this.hasReceivedInitialResponse || this.recoveryPollingActive) {
                this.clearInitialUpdateGraceTimeout();
                return;
            }
            this.scheduleInitialUpdateGraceTimeout();
        },
        stopChatRecoveryPolling() {
            if (this.recoveryPollTimer) {
                clearTimeout(this.recoveryPollTimer);
                this.recoveryPollTimer = null;
            }
            this.recoveryPollingActive = false;
            this.recoveryPollAttempts = 0;
            const controller = this.recoveryPollController;
            this.recoveryPollController = null;
            if (controller) controller.abort();
        },
        isHeartbeatUpdate(data) {
            return !!(
                data &&
                data.heartbeat &&
                !data.done &&
                !data.error &&
                (data.text === undefined || data.text === null || data.text === '')
            );
        },
        getPolledStatusTimeoutSeconds(data) {
            const phase = data && data.phase ? String(data.phase) : '';
            if (phase && this.CHAT_PHASE_TIMEOUT_SECONDS[phase]) {
                return this.CHAT_PHASE_TIMEOUT_SECONDS[phase];
            }
            return Math.floor(this.CHAT_UPDATE_TIMEOUT_MS / 1000);
        },
        isStalledPolledStatus(data) {
            if (
                !data
                || data.done
                || data.is_complete
                || data.error
                || data.cancelled
                || data.waiting_for_user === true
                || data.waitingForUser === true
                || data.phase === 'waiting_user'
                || data.status === 'done'
            ) {
                return false;
            }
            const timeoutSeconds = this.getPolledStatusTimeoutSeconds(data);
            const phaseReferenceTime = Number(data.phase_started_at || data.started_at || 0);
            const visibleProgressReferenceTime = Number(data.last_progress_at || data.phase_last_progress_at || 0);
            // A new provider phase gets its own allowance after a long tool.
            const progressReferenceTime = Math.max(visibleProgressReferenceTime, phaseReferenceTime);
            const progressAge = progressReferenceTime ? ((Date.now() / 1000) - progressReferenceTime) : null;

            const heartbeatReferenceTime = Number(data.last_heartbeat_at || 0);
            const heartbeatAge = heartbeatReferenceTime ? ((Date.now() / 1000) - heartbeatReferenceTime) : null;
            const phaseHasStrictTimeout = !!(data.phase && this.CHAT_PHASE_TIMEOUT_SECONDS[String(data.phase)]);
            if (
                phaseHasStrictTimeout &&
                progressAge !== null &&
                progressAge > timeoutSeconds
            ) {
                return true;
            }

            if (heartbeatAge !== null && heartbeatAge <= this.CHAT_HEARTBEAT_FRESH_SECONDS) {
                if (progressAge === null) {
                    return false;
                }
                return progressAge > this.CHAT_HEARTBEAT_GRACE_SECONDS;
            }

            return progressAge !== null && progressAge > this.CHAT_UPDATE_TIMEOUT_MS / 1000;
        },
        canonicalRecoveryIdentity() {
            const message = this.message || {};
            return {
                responseMessageId: durableChatResponseId(
                    message.response_message_id || message.message_id
                ),
                triggerMessageId: String(
                    message.ai_response_anchor_message_id
                    || message.trigger_message_id
                    || ''
                ).trim(),
                turnId: String(message.turn_id || '').trim(),
                runId: this.getCurrentKnownRunId() || '',
                clientRequestId: String(
                    message.client_request_id || message.clientRequestId || ''
                ).trim(),
            };
        },
        canonicalRecoveryUserMessage(conversation, identity) {
            const users = (Array.isArray(conversation) ? conversation : [])
                .filter(item => item && item.role === 'user');
            const uniqueMatch = predicate => {
                const matches = users.filter(predicate);
                return matches.length === 1 ? matches[0] : null;
            };
            if (identity.clientRequestId) {
                const clientMatch = uniqueMatch(item => (
                    String(item.client_request_id || '').trim() === identity.clientRequestId
                ));
                if (clientMatch) return clientMatch;
            }
            if (identity.triggerMessageId) {
                const triggerMatch = uniqueMatch(item => (
                    String(item.message_id || '').trim() === identity.triggerMessageId
                ));
                if (triggerMatch) return triggerMatch;
            }
            if (identity.turnId) {
                return uniqueMatch(item => (
                    String(item.turn_id || '').trim() === identity.turnId
                ));
            }
            return null;
        },
        canonicalRecoveryAssistantMessage(conversation, identity, userMessage = null) {
            const rows = Array.isArray(conversation) ? conversation : [];
            const assistants = rows.filter(item => (
                item
                && item.role === 'assistant'
                && typeof item.content === 'string'
                && item.content.length > 0
            ));
            const uniqueMatch = predicate => {
                const matches = assistants.filter(predicate);
                return matches.length === 1 ? matches[0] : null;
            };
            if (identity.responseMessageId) {
                const responseMatch = uniqueMatch(item => (
                    [item.message_id, item.response_message_id]
                        .map(value => String(value || '').trim())
                        .includes(identity.responseMessageId)
                ));
                if (responseMatch) return responseMatch;
                // Once the server has reserved a durable response id, a turn
                // or run match must not substitute a different assistant row.
                return null;
            }
            const effectiveTurnId = identity.turnId || String(
                userMessage && userMessage.turn_id || ''
            ).trim();
            if (effectiveTurnId) {
                const turnMatch = uniqueMatch(item => (
                    String(item.turn_id || '').trim() === effectiveTurnId
                ));
                if (turnMatch) return turnMatch;
            }
            if (userMessage) {
                const userIndex = rows.indexOf(userMessage);
                if (userIndex >= 0) {
                    for (let index = userIndex + 1; index < rows.length; index += 1) {
                        const item = rows[index];
                        if (!item) continue;
                        if (item.role === 'user') break;
                        if (
                            item.role === 'assistant'
                            && typeof item.content === 'string'
                            && item.content.length > 0
                        ) return item;
                    }
                }
            }
            return null;
        },
        canonicalRecoveryReceiptMatches(receipt, identity, userMessage = null) {
            if (!receipt || typeof receipt !== 'object') return false;
            const receiptResponseId = String(receipt.response_message_id || '').trim();
            if (
                identity.responseMessageId
                && receiptResponseId
                && receiptResponseId !== identity.responseMessageId
            ) return false;
            const expected = {
                responseMessageId: identity.responseMessageId,
                triggerMessageId: identity.triggerMessageId || String(
                    userMessage && userMessage.message_id || ''
                ).trim(),
                turnId: identity.turnId || String(
                    userMessage && userMessage.turn_id || ''
                ).trim(),
            };
            return [
                ['response_message_id', expected.responseMessageId],
                ['trigger_message_id', expected.triggerMessageId],
                ['turn_id', expected.turnId],
            ].some(([field, value]) => (
                !!value && String(receipt[field] || '').trim() === value
            ));
        },
        canonicalRecoveryReceipts(payload) {
            const serverReceipts = Array.isArray(payload && payload.tool_receipts)
                ? payload.tool_receipts
                : [];
            const parentSession = this.$parent && this.$parent.session;
            const localReceipts = parentSession && Array.isArray(parentSession.tool_receipts)
                ? parentSession.tool_receipts
                : [];
            const byId = new Map();
            [...localReceipts, ...serverReceipts].forEach((receipt) => {
                if (!receipt || typeof receipt !== 'object') return;
                const id = String(receipt.tool_run_id || '').trim();
                if (!id) return;
                const current = byId.get(id);
                const currentVersion = Number(current && current.state_version || 0);
                const nextVersion = Number(receipt.state_version || 0);
                if (!current || nextVersion >= currentVersion) byId.set(id, receipt);
            });
            return Array.from(byId.values());
        },
        latestCanonicalRecoveryReceipt(receipts, predicate) {
            return (Array.isArray(receipts) ? receipts : [])
                .filter(predicate)
                .sort((left, right) => (
                    Number(right.event_seq || 0) - Number(left.event_seq || 0)
                    || Number(right.state_version || 0) - Number(left.state_version || 0)
                ))[0] || null;
        },
        canonicalRecoveryStatusMatches(status, identity, userMessage = null) {
            if (!status || typeof status !== 'object' || !Object.keys(status).length) return false;
            const statusResponseId = String(status.response_message_id || '').trim();
            if (
                identity.responseMessageId
                && statusResponseId
                && statusResponseId !== identity.responseMessageId
            ) return false;
            const expected = {
                responseMessageId: identity.responseMessageId,
                triggerMessageId: identity.triggerMessageId || String(
                    userMessage && userMessage.message_id || ''
                ).trim(),
                turnId: identity.turnId || String(
                    userMessage && userMessage.turn_id || ''
                ).trim(),
                runId: String(identity.runId || '').trim(),
            };
            return [
                ['response_message_id', expected.responseMessageId],
                ['trigger_message_id', expected.triggerMessageId],
                ['turn_id', expected.turnId],
                ['run_id', expected.runId],
            ].some(([field, value]) => (
                !!value && String(status[field] || '').trim() === value
            ));
        },
        applyCanonicalRecoveryAnswer(answer, payload, identity, userMessage = null) {
            if (!answer || !this.message || !this.message.isLoading) return false;
            const responseMessageId = String(
                answer.response_message_id || answer.message_id || ''
            ).trim();
            const reservedResponseId = durableChatResponseId(
                this.message.response_message_id || this.message.message_id
            );
            if (
                reservedResponseId
                && responseMessageId
                && reservedResponseId !== responseMessageId
            ) return false;

            const runStatus = payload && payload.run_status && typeof payload.run_status === 'object'
                ? payload.run_status
                : {};
            const triggerMessageId = String(
                userMessage && userMessage.message_id
                || runStatus.trigger_message_id
                || identity.triggerMessageId
                || ''
            ).trim();
            const turnId = String(
                answer.turn_id
                || (userMessage && userMessage.turn_id)
                || runStatus.turn_id
                || identity.turnId
                || ''
            ).trim();
            const runId = String(
                answer.run_id || runStatus.run_id || identity.runId || ''
            ).trim();
            try { this.$set(this.message, 'awaitingInitialRequestAcceptance', false); } catch (e) { this.message.awaitingInitialRequestAcceptance = false; }
            if (responseMessageId) {
                try { this.$set(this.message, 'response_message_id', responseMessageId); } catch (e) { this.message.response_message_id = responseMessageId; }
                try { this.$set(this.message, 'message_id', responseMessageId); } catch (e) { this.message.message_id = responseMessageId; }
            }
            if (triggerMessageId) {
                try { this.$set(this.message, 'ai_response_anchor_message_id', triggerMessageId); } catch (e) { this.message.ai_response_anchor_message_id = triggerMessageId; }
            }
            if (turnId) {
                try { this.$set(this.message, 'turn_id', turnId); } catch (e) { this.message.turn_id = turnId; }
            }
            if (runId) {
                try { this.$set(this.message, 'run_id', runId); } catch (e) { this.message.run_id = runId; }
                this.activeRunId = runId;
            }
            this.message.text = answer.content;
            if (answer.response_audio_source) this.message.response_audio_source = answer.response_audio_source;
            this.message.isLoading = false;
            this.message.cancelled = false;
            this.message.waitingForUser = false;
            this.message.failed = false;
            this.message.retryable = false;
            if (this.isResumedLoadingState) {
                this.clearResumeTimeout();
                this.isResumedLoadingState = false;
            }
            if (this.isReasoningDuringChat) {
                this.isReasoningDuringChat = false;
                this.removeReasoningIndicator();
            }
            this.maybeHandleFirstVisibleAnswerText();
            if (Array.isArray(payload && payload.suggested_questions)) {
                this.$emit('suggestedQuestionsReceived', payload.suggested_questions);
            }
            this.clearChatUpdateTimeout();
            this.clearInitialUpdateGraceTimeout();
            this.stopChatRecoveryPolling();
            this.unsubscribeFromResearchActivity();
            this.unsubscribeFromChatUpdates();
            this.chatSequence = 0;
            this.chatChunkState = { groups: {}, completedIds: [] };
            this.$emit('messageCompleted', this.message);
            return true;
        },
        applyCanonicalRecoveryReceipt(receipt) {
            if (!receipt || !this.message) return false;
            const resultType = String(receipt.result_ref && receipt.result_ref.type || '').trim();
            const isWaitingContext = resultType === 'context_request'
                && String(receipt.status || '') === 'waiting_user';
            const isContinuation = resultType === 'continuation';
            if (!isWaitingContext && !isContinuation) return false;

            const responseMessageId = String(receipt.response_message_id || '').trim();
            const triggerMessageId = String(receipt.trigger_message_id || '').trim();
            const turnId = String(receipt.turn_id || '').trim();
            try { this.$set(this.message, 'awaitingInitialRequestAcceptance', false); } catch (e) { this.message.awaitingInitialRequestAcceptance = false; }
            if (responseMessageId) {
                try { this.$set(this.message, 'response_message_id', responseMessageId); } catch (e) { this.message.response_message_id = responseMessageId; }
                try { this.$set(this.message, 'message_id', responseMessageId); } catch (e) { this.message.message_id = responseMessageId; }
            }
            if (triggerMessageId) {
                try { this.$set(this.message, 'ai_response_anchor_message_id', triggerMessageId); } catch (e) { this.message.ai_response_anchor_message_id = triggerMessageId; }
            }
            if (turnId) {
                try { this.$set(this.message, 'turn_id', turnId); } catch (e) { this.message.turn_id = turnId; }
            }
            if (isContinuation) {
                const continuationJobId = String(receipt.result_ref.id || '').trim();
                try { this.$set(this.message, 'continuation_job_id', continuationJobId); } catch (e) { this.message.continuation_job_id = continuationJobId; }
                const needsAction = ['failed', 'cancelled', 'expired'].includes(
                    String(receipt.status || '')
                );
                try { this.$set(this.message, 'continuationNeedsAction', needsAction); } catch (e) { this.message.continuationNeedsAction = needsAction; }
            }
            this.message.isLoading = false;
            this.message.waitingForUser = true;
            this.message.cancelled = false;
            this.message.failed = false;
            this.message.retryable = false;
            this.clearChatUpdateTimeout();
            this.clearInitialUpdateGraceTimeout();
            this.stopChatRecoveryPolling();
            this.unsubscribeFromResearchActivity();
            this.unsubscribeFromChatUpdates();
            this.chatSequence = 0;
            this.chatChunkState = { groups: {}, completedIds: [] };
            this.$emit('messageWaitingForContext', this.message);
            return true;
        },
        finishCanonicalRecoveryError(errorData, { recoverable, message } = {}) {
            if (!this.message || !this.message.isLoading || !this.isLatestBotMessage()) return false;
            const identity = this.canonicalRecoveryIdentity();
            try { this.$set(this.message, 'awaitingInitialRequestAcceptance', false); } catch (e) { this.message.awaitingInitialRequestAcceptance = false; }
            const responseMessageId = String(
                identity.responseMessageId || errorData && errorData.response_message_id || ''
            ).trim();
            const triggerMessageId = String(
                identity.triggerMessageId || errorData && errorData.trigger_message_id || ''
            ).trim();
            const turnId = String(
                identity.turnId || errorData && errorData.turn_id || ''
            ).trim();
            const runId = String(
                identity.runId || errorData && errorData.run_id || ''
            ).trim();
            if (responseMessageId) {
                try { this.$set(this.message, 'response_message_id', responseMessageId); } catch (e) { this.message.response_message_id = responseMessageId; }
                try { this.$set(this.message, 'message_id', responseMessageId); } catch (e) { this.message.message_id = responseMessageId; }
            }
            if (triggerMessageId) {
                try { this.$set(this.message, 'ai_response_anchor_message_id', triggerMessageId); } catch (e) { this.message.ai_response_anchor_message_id = triggerMessageId; }
            }
            if (turnId) {
                try { this.$set(this.message, 'turn_id', turnId); } catch (e) { this.message.turn_id = turnId; }
            }
            if (runId) {
                try { this.$set(this.message, 'run_id', runId); } catch (e) { this.message.run_id = runId; }
                this.activeRunId = runId;
            }
            if (this.isResumedLoadingState) {
                this.clearResumeTimeout();
                this.isResumedLoadingState = false;
            }
            if (this.isReasoningDuringChat) {
                this.isReasoningDuringChat = false;
                this.removeReasoningIndicator();
            }
            this.suppressInitialResponseDetection = true;
            this.message.text = message
                || (errorData && (errorData.error_message || errorData.message))
                || "No pudimos recuperar la respuesta en curso. Por favor, intenta de nuevo.";
            this.message.isLoading = false;
            this.message.cancelled = false;
            this.message.waitingForUser = false;
            this.message.failed = true;
            this.message.retryable = recoverable !== false;
            this.clearChatUpdateTimeout();
            this.clearInitialUpdateGraceTimeout();
            this.stopChatRecoveryPolling();
            this.unsubscribeFromResearchActivity();
            this.unsubscribeFromChatUpdates();
            this.chatSequence = 0;
            this.chatChunkState = { groups: {}, completedIds: [] };
            this.$emit('messageError', this.message);
            return true;
        },
        scheduleCanonicalRecoveryPoll() {
            if (
                this.recoveryPollingActive
                || this.recoveryPollTimer
                || !this.message.isLoading
                || !this.isLatestBotMessage()
            ) return;
            // Let the canonical request settle before polling again. Starting
            // synchronously here would let another empty-cache 500 coalesce
            // onto the request that just finished and leave no future poll.
            this.recoveryPollTimer = setTimeout(() => {
                this.recoveryPollTimer = null;
                this.startChatRecoveryPolling('canonical-active');
            }, 2000);
        },
        async performCanonicalRecoveryBeforeError(errorData = {}) {
            const chatUuid = String(window.chat_uuid || '').trim();
            const targetMessage = this.message;
            if (!chatUuid || !targetMessage || !targetMessage.isLoading || !this.isLatestBotMessage()) {
                return { state: 'stale' };
            }

            let response;
            let payload;
            let recoveryAbortTimer = null;
            try {
                const query = new URLSearchParams({
                    uuid: chatUuid,
                    chat_pagination: '1',
                    limit: '50',
                });
                const recoveryAbortController = typeof AbortController !== 'undefined'
                    ? new AbortController()
                    : null;
                if (recoveryAbortController) {
                    recoveryAbortTimer = setTimeout(
                        () => recoveryAbortController.abort(),
                        CANONICAL_CHAT_RECOVERY_TIMEOUT_MS,
                    );
                }
                response = await fetch(`/chats/get?${query.toString()}`, {
                    ...(recoveryAbortController
                        ? { signal: recoveryAbortController.signal }
                        : {}),
                });
                payload = await response.json().catch(() => ({}));
            } catch (error) {
                payload = null;
            } finally {
                if (recoveryAbortTimer) clearTimeout(recoveryAbortTimer);
            }
            if (
                targetMessage !== this.message
                || String(window.chat_uuid || '').trim() !== chatUuid
                || !this.message.isLoading
                || !this.isLatestBotMessage()
            ) return { state: 'stale' };

            if (!response || !response.ok || !payload || typeof payload !== 'object') {
                this.finishCanonicalRecoveryError(errorData, {
                    recoverable: false,
                    message: "No pudimos verificar el estado de esta respuesta. Vuelve a conectarte y abre el chat antes de intentarlo de nuevo.",
                });
                return { state: 'outcome-unknown' };
            }

            const identity = this.canonicalRecoveryIdentity();
            const conversation = Array.isArray(payload.conversation) ? payload.conversation : [];
            const userMessage = this.canonicalRecoveryUserMessage(conversation, identity);
            const answer = this.canonicalRecoveryAssistantMessage(
                conversation,
                identity,
                userMessage,
            );
            if (answer && answer.generation_failed === true) {
                this.finishCanonicalRecoveryError({
                    ...answer,
                    response_message_id: answer.response_message_id || answer.message_id,
                    trigger_message_id: userMessage && userMessage.message_id,
                }, {
                    recoverable: answer.recoverable !== false,
                    message: answer.content,
                });
                return { state: 'failed' };
            }
            if (answer && this.applyCanonicalRecoveryAnswer(answer, payload, identity, userMessage)) {
                return { state: 'answered' };
            }

            const receipts = this.canonicalRecoveryReceipts(payload).filter(receipt => (
                this.canonicalRecoveryReceiptMatches(receipt, identity, userMessage)
            ));
            const actionableReceipt = this.latestCanonicalRecoveryReceipt(receipts, receipt => {
                const resultType = String(receipt.result_ref && receipt.result_ref.type || '');
                return resultType === 'continuation'
                    || (
                        resultType === 'context_request'
                        && String(receipt.status || '') === 'waiting_user'
                    );
            });
            if (actionableReceipt && this.applyCanonicalRecoveryReceipt(actionableReceipt)) {
                return { state: 'receipt' };
            }

            const runStatus = payload.run_status && typeof payload.run_status === 'object'
                ? payload.run_status
                : {};
            const statusMatches = this.canonicalRecoveryStatusMatches(
                runStatus,
                identity,
                userMessage,
            );
            if (payload.in_progress === true) {
                if (statusMatches) {
                    this.acceptChatRunUpdate(runStatus);
                    if (Array.isArray(payload.reasoning_activities)) {
                        this.mergeResearchActivitiesFromServer(payload.reasoning_activities);
                    }
                }
                this.scheduleCanonicalRecoveryPoll();
                return { state: statusMatches ? 'active' : 'active-indeterminate' };
            }

            const committedSideEffect = this.latestCanonicalRecoveryReceipt(receipts, receipt => (
                ['crear_editar_archivo', 'revisar_documento'].includes(String(receipt.tool_name || ''))
                && ['running', 'waiting_user', 'completed', 'partial'].includes(String(receipt.status || ''))
            ));
            if (committedSideEffect) {
                this.finishCanonicalRecoveryError(errorData, {
                    recoverable: false,
                    message: "La operación de este turno ya tiene un resultado guardado. Actualiza el chat para recuperar la respuesta sin repetirla.",
                });
                return { state: 'side-effect-committed' };
            }

            if (payload.tool_receipts_has_more === true) {
                this.finishCanonicalRecoveryError(errorData, {
                    recoverable: false,
                    message: "No pudimos verificar todavía todos los resultados de este turno. Actualiza el chat antes de intentar enviarlo de nuevo.",
                });
                return { state: 'receipt-history-incomplete' };
            }

            if (payload.run_state_indeterminate === true) {
                this.finishCanonicalRecoveryError(errorData, {
                    recoverable: false,
                    message: "No pudimos verificar el estado de esta respuesta. Vuelve a conectarte y abre el chat antes de intentarlo de nuevo.",
                });
                return { state: 'outcome-unknown' };
            }

            if (
                statusMatches
                && (
                    runStatus.waiting_for_user === true
                    || runStatus.phase === 'waiting_user'
                    || runStatus.status === 'waiting_user'
                )
            ) {
                this.handleChatUpdate({
                    ...runStatus,
                    done: true,
                    waiting_for_user: true,
                    replace: true,
                });
                return { state: 'waiting-user' };
            }

            if (payload.was_cancelled === true) {
                try { this.$set(this.message, 'awaitingInitialRequestAcceptance', false); } catch (e) { this.message.awaitingInitialRequestAcceptance = false; }
                this.handleChatUpdate({
                    done: true,
                    cancelled: true,
                    text: typeof this.message.text === 'string' ? this.message.text : '',
                    run_id: identity.runId || undefined,
                    response_message_id: identity.responseMessageId || undefined,
                    trigger_message_id: identity.triggerMessageId || undefined,
                    turn_id: identity.turnId || undefined,
                });
                return { state: 'cancelled' };
            }

            const correlatedFailure = statusMatches && String(runStatus.status || '') === 'error'
                ? runStatus
                : {};
            this.finishCanonicalRecoveryError({
                ...errorData,
                ...correlatedFailure,
                run_id: identity.runId || correlatedFailure.run_id || undefined,
                response_message_id: identity.responseMessageId
                    || correlatedFailure.response_message_id
                    || undefined,
                trigger_message_id: identity.triggerMessageId
                    || String(userMessage && userMessage.message_id || '').trim()
                    || correlatedFailure.trigger_message_id
                    || undefined,
                turn_id: identity.turnId
                    || String(userMessage && userMessage.turn_id || '').trim()
                    || correlatedFailure.turn_id
                    || undefined,
            }, {
                recoverable: errorData.recoverable !== false,
            });
            return { state: 'failed' };
        },
        reconcileCanonicalBeforeTerminalError(errorData = {}) {
            if (this.canonicalRecoveryInFlight) return this.canonicalRecoveryInFlight;
            const flight = this.performCanonicalRecoveryBeforeError(errorData).catch((error) => {
                this.logAiFallbackTest('canonical-recovery-error', this.buildChatFallbackLogDetails(errorData, {
                    error,
                }));
                this.finishCanonicalRecoveryError(errorData, {
                    recoverable: false,
                    message: "No pudimos verificar el estado de esta respuesta. Vuelve a conectarte y abre el chat antes de intentarlo de nuevo.",
                });
                return { state: 'outcome-unknown' };
            });
            this.canonicalRecoveryInFlight = flight;
            return flight.finally(() => {
                if (this.canonicalRecoveryInFlight === flight) {
                    this.canonicalRecoveryInFlight = null;
                }
            });
        },
        applyPolledChatStatus(data) {
            if (!data || !this.isLatestBotMessage()) return { accepted: false, done: false };
            if (data.is_complete === true && !data.done) {
                data = { ...data, done: true };
            }
            if (!this.message.isLoading) {
                this.stopChatRecoveryPolling();
                return { accepted: false, done: true };
            }
            if (this.ignoreCompletedPolledRun(data, 'check_chat_status')) {
                return { accepted: false, done: false };
            }
            if (!this.acceptChatRunUpdate(data)) return { accepted: false, done: false };
            this.updateChatProgressFromStatus(data);
            if (data.document_previews && typeof data.document_previews === 'object') {
                window.dispatchEvent(new CustomEvent('lexius:document-preview-discovery', {
                    detail: { chat_uuid: window.chat_uuid, document_previews: data.document_previews },
                }));
            }
            if (Array.isArray(data.reasoning_activities)) {
                this.mergeResearchActivitiesFromServer(data.reasoning_activities);
            }
            this.logAiFallbackTest('poll-update-applied', this.buildChatFallbackLogDetails(data, {
                source: 'check_chat_status',
            }));

            if (this.isStalledPolledStatus(data)) {
                this.stopChatRecoveryPolling();
                this.logAiFallbackTest('poll-stalled', this.buildChatFallbackLogDetails(data, {
                    source: 'check_chat_status',
                }));
                this.handleChatUpdate({
                    error: true,
                    error_message: "La respuesta en curso dejó de avanzar. Por favor, intenta enviarla de nuevo.",
                    run_id: this.normalizeRunId(data.run_id) || this.getCurrentKnownRunId(),
                });
                return { accepted: true, done: true };
            }

            this.markChatLiveness('poll-status', {
                clearInitialGrace: true,
                resetTimeout: true,
                stopRecoveryPolling: false,
            });

            const fullText = typeof data.message === 'string' ? data.message : '';
            if (shouldHydrateInFlightChatText({
                placeholderText: this.message.text,
                snapshotText: fullText,
                snapshotDone: !!(data.done || data.is_complete),
                ...this.staleInFlightChatUpdateArgs(data),
            })) {
                if (fullText.startsWith(this.message.text || '')) {
                    this.message.text = fullText;
                } else if (fullText !== this.message.text) {
                    this.message.text = fullText;
                }
                this.maybeHandleFirstVisibleAnswerText();
            }

            if (Array.isArray(data.suggested_questions)) {
                this.message.suggestedQuestions = data.suggested_questions;
            }

            if (data.cancelled) {
                this.handleChatUpdate({
                    done: true,
                    cancelled: true,
                    text: fullText,
                    run_id: this.normalizeRunId(data.run_id) || this.getCurrentKnownRunId(),
                });
                return { accepted: true, done: true };
            }

            if (data.waiting_for_user === true || data.waitingForUser === true || data.phase === 'waiting_user' || data.status === 'waiting_user') {
                this.handleChatUpdate({
                    done: true,
                    waiting_for_user: true,
                    text: fullText,
                    replace: true,
                    run_id: this.normalizeRunId(data.run_id) || this.getCurrentKnownRunId(),
                    response_message_id: data.response_message_id,
                    trigger_message_id: data.trigger_message_id,
                    turn_id: data.turn_id,
                });
                return { accepted: true, done: true };
            }

            if (data.done) {
                this.handleChatUpdate({
                    done: true,
                    text: fullText,
                    suggested_questions: data.suggested_questions || null,
                    replace: true,
                    run_id: this.normalizeRunId(data.run_id) || this.getCurrentKnownRunId(),
                });
                return { accepted: true, done: true };
            }

            return { accepted: true, done: false };
        },
        async seedResearchActivityHistoryFromServer(options = {}) {
            const reasoningOnly = options.reasoningOnly === true;
            const force = options.force === true;
            if (this.reasoningSeedInFlight || (this.reasoningSeedRequested && !force)) return;
            if (
                !window.chat_uuid
                || !this.shouldReceiveResearchActivity()
                || !this.isLatestBotMessage()
                || this.message.awaitingInitialRequestAcceptance
            ) return;
            this.reasoningSeedRequested = true;
            this.reasoningSeedInFlight = true;
            try {
                const continuationJobId = String(
                    this.message && this.message.continuation_job_id || ''
                ).trim();
                const response = continuationJobId
                    ? await fetch(
                        `/chat/continuations/${encodeURIComponent(continuationJobId)}?${new URLSearchParams({
                            chat_uuid: String(window.chat_uuid || ''),
                        }).toString()}`
                    )
                    : await fetch('/check_chat_status', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ uuid: window.chat_uuid })
                    });
                let data = null;
                try {
                    data = await response.json();
                } catch (e) {
                    data = null;
                }
                if (!data || !this.shouldReceiveResearchActivity() || !this.isLatestBotMessage()) return;
                if (continuationJobId) {
                    if (Array.isArray(data.reasoning_activities)) {
                        this.mergeResearchActivitiesFromServer(data.reasoning_activities);
                    }
                    return;
                }
                // A durable continuation intentionally leaves the ordinary
                // chat runtime at waiting_user. Applying that stale status
                // would pause the bubble again, so recovery in this state is
                // limited to the separately persisted reasoning chain.
                if (reasoningOnly || !this.message.isLoading) return;
                if (response.ok) {
                    if (this.ignoreCompletedPolledRun(data, 'check_chat_status_seed')) {
                        return;
                    }
                    // Correlate the snapshot before it can mutate the visible
                    // bubble. A runless terminal status may belong to an older
                    // request even though this component is currently active.
                    if (!this.acceptChatRunUpdate(data)) return;
                    if (Array.isArray(data.reasoning_activities)) {
                        this.mergeResearchActivitiesFromServer(data.reasoning_activities);
                    }
                    const fullText = typeof data.message === 'string' ? data.message : '';
                    if (shouldHydrateInFlightChatText({
                        placeholderText: this.message.text,
                        snapshotText: fullText,
                        snapshotDone: !!(data.done || data.is_complete),
                        ...this.staleInFlightChatUpdateArgs(data),
                    })) {
                        this.message.text = fullText;
                        this.maybeHandleFirstVisibleAnswerText();
                    }
                    if (data.done || data.is_complete || data.cancelled || data.waiting_for_user === true || data.waitingForUser === true || data.phase === 'waiting_user' || data.status === 'waiting_user') {
                        this.applyPolledChatStatus(data);
                    }
                }
            } catch (error) {
                this.logAiFallbackTest('reasoning-seed-error', this.buildChatFallbackLogDetails(null, {
                    source: 'check_chat_status',
                    error,
                }));
            } finally {
                this.reasoningSeedInFlight = false;
            }
        },
        async recoverContinuationReasoningOnce() {
            if (
                !this.continuationReasoningRecoveryActive
                || !this.isLatestBotMessage()
                || this.message.waitingForUser !== true
                || this.message.resumingContext !== true
            ) {
                this.stopContinuationReasoningRecovery();
                return;
            }

            await this.seedResearchActivityHistoryFromServer({
                reasoningOnly: true,
                force: true,
            });

            if (
                this.continuationReasoningRecoveryActive
                && this.isLatestBotMessage()
                && this.message.waitingForUser === true
                && this.message.resumingContext === true
            ) {
                this.continuationReasoningRecoveryTimer = setTimeout(
                    () => this.recoverContinuationReasoningOnce(),
                    this.CONTINUATION_REASONING_RECOVERY_MS,
                );
            }
        },
        startContinuationReasoningRecovery() {
            if (this.continuationReasoningRecoveryActive) return;
            this.continuationReasoningRecoveryActive = true;
            this.recoverContinuationReasoningOnce();
        },
        stopContinuationReasoningRecovery() {
            this.continuationReasoningRecoveryActive = false;
            if (this.continuationReasoningRecoveryTimer) {
                clearTimeout(this.continuationReasoningRecoveryTimer);
                this.continuationReasoningRecoveryTimer = null;
            }
        },
        async pollChatStatusOnce() {
            if (!this.recoveryPollingActive || this.recoveryPollController) return;
            if (!window.chat_uuid || !this.message.isLoading || !this.isLatestBotMessage()) {
                this.stopChatRecoveryPolling();
                return;
            }

            this.recoveryPollAttempts += 1;
            const chatUuid = window.chat_uuid;
            const targetMessage = this.message;
            const controller = new AbortController();
            this.recoveryPollController = controller;
            const isCurrentPoll = () => (
                this.recoveryPollController === controller
                && this.recoveryPollingActive
                && window.chat_uuid === chatUuid
                && this.message === targetMessage
                && this.message.isLoading
                && this.isLatestBotMessage()
            );
            const abortTimer = setTimeout(() => controller.abort(), CHAT_RECOVERY_POLL_TIMEOUT_MS);
            let shouldRetry = false;

            try {
                this.logAiFallbackTest('poll-attempt', this.buildChatFallbackLogDetails(null, {
                    source: 'check_chat_status',
                    attempt: this.recoveryPollAttempts,
                }));
                const response = await fetch('/check_chat_status', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ uuid: chatUuid }),
                    signal: controller.signal,
                });

                if (response.ok) {
                    const data = await response.json();
                    if (!isCurrentPoll()) return;
                    const pollResult = this.applyPolledChatStatus(data);
                    this.logAiFallbackTest('poll-response', this.buildChatFallbackLogDetails(data, {
                        source: 'check_chat_status',
                        attempt: this.recoveryPollAttempts,
                        http_status: response.status,
                        accepted: !!(pollResult && pollResult.accepted),
                        terminal: !!(pollResult && pollResult.done),
                    }));

                    if (pollResult && pollResult.done) {
                        this.stopChatRecoveryPolling();
                        return;
                    }
                } else {
                    let errorPayload = null;
                    try {
                        errorPayload = await response.json();
                    } catch (e) {
                        if (controller.signal.aborted) throw e;
                        errorPayload = null;
                    }
                    if (!isCurrentPoll()) return;
                    this.logAiFallbackTest('poll-http-error', this.buildChatFallbackLogDetails(errorPayload, {
                        source: 'check_chat_status',
                        attempt: this.recoveryPollAttempts,
                        http_status: response.status,
                        error_message: errorPayload && errorPayload.message ? errorPayload.message : null,
                    }));

                    if (response.status >= 500) {
                        if (!this.message.isLoading || !this.isLatestBotMessage() || this.hasDeliveredAnswerLocally()) {
                            this.stopChatRecoveryPolling();
                            return;
                        }
                        this.stopChatRecoveryPolling();
                        this.handleChatUpdate({
                            error: true,
                            error_message: errorPayload && errorPayload.message
                                ? errorPayload.message
                                : "No pudimos recuperar la respuesta en curso. Por favor, intenta de nuevo.",
                            recoverable: !errorPayload || errorPayload.recoverable !== false,
                            run_id: this.normalizeRunId(errorPayload && errorPayload.run_id) || this.getCurrentKnownRunId(),
                            response_message_id: errorPayload && errorPayload.response_message_id,
                            trigger_message_id: errorPayload && errorPayload.trigger_message_id,
                            turn_id: errorPayload && errorPayload.turn_id,
                        });
                        return;
                    }
                }
            } catch (error) {
                if (!isCurrentPoll()) return;
                this.logAiFallbackTest('poll-network-error', this.buildChatFallbackLogDetails(null, {
                    source: 'check_chat_status',
                    attempt: this.recoveryPollAttempts,
                    error,
                }));
                if (this.recoveryPollAttempts > 120) {
                    if (!this.message.isLoading || !this.isLatestBotMessage() || this.hasDeliveredAnswerLocally()) {
                        this.stopChatRecoveryPolling();
                        return;
                    }
                    this.stopChatRecoveryPolling();
                    this.handleChatUpdate({
                        error: true,
                        error_message: "No pudimos recuperar la respuesta en curso. Por favor, intenta de nuevo.",
                        run_id: this.getCurrentKnownRunId(),
                    });
                    return;
                }
            } finally {
                clearTimeout(abortTimer);
                shouldRetry = isCurrentPoll();
                if (this.recoveryPollController === controller) {
                    this.recoveryPollController = null;
                    if (!shouldRetry) this.stopChatRecoveryPolling();
                }
            }

            if (!shouldRetry) return;
            const nextDelay = this.recoveryPollAttempts < 30 ? 2000 : 5000;
            this.recoveryPollTimer = setTimeout(() => {
                this.recoveryPollTimer = null;
                this.pollChatStatusOnce();
            }, nextDelay);
        },
        startChatRecoveryPolling(reason = 'unknown') {
            if (
                this.recoveryPollingActive
                || !this.message.isLoading
                || !this.isLatestBotMessage()
                || this.message.awaitingInitialRequestAcceptance
            ) {
                return;
            }

            console.warn(`BotMessage: Starting chat recovery polling (${reason})`);
            this.logAiFallbackTest('recovery-polling-start', this.buildChatFallbackLogDetails(null, {
                reason,
            }));
            this.recoveryPollingActive = true;
            this.recoveryPollAttempts = 0;
            this.clearInitialUpdateGraceTimeout();
            this.pollChatStatusOnce();
        },
        resetChatUpdateTimeout() {
            this.clearChatUpdateTimeout();

            this.chatUpdateTimeout = setTimeout(() => {
                const timeoutSeconds = Math.floor(CHAT_RECOVERY_SILENCE_MS / 1000);
                console.warn(`No chat updates received for ${timeoutSeconds} seconds; checking saved response`);
                this.startChatRecoveryPolling('timeout');
            }, CHAT_RECOVERY_SILENCE_MS);
        },
        clearChatUpdateTimeout() {
            if (this.chatUpdateTimeout) {
                clearTimeout(this.chatUpdateTimeout);
                this.chatUpdateTimeout = null;
            }
        },
        startResumeTimeout() {
            this.clearResumeTimeout();

            // Set a 20-second timeout for resumed loading states
            this.resumeTimeout = setTimeout(() => {
                console.error('Resume timeout - no Ably updates received within 20 seconds for resumed loading state');

                this.handleChatUpdate({
                    error: true,
                    error_message: "La sesión se ha restaurado pero no se obtuvieron actualizaciones del servidor. Por favor, intenta enviar el mensaje de nuevo.",
                    run_id: this.getCurrentKnownRunId(),
                });
            }, 20000); // 20 seconds
        },
        clearResumeTimeout() {
            if (this.resumeTimeout) {
                clearTimeout(this.resumeTimeout);
                this.resumeTimeout = null;
            }
        },
        resetRealtimeActivityState() {
            this.hasReceivedRealtimeActivity = false;
            this.hasReceivedChatRealtimeActivity = false;
            this.lastLivenessSignalType = null;
            this.lastLivenessAt = null;
            this.progressWaitStartedAt = Date.now();
            this.lastProgressAt = null;
            this.lastWorkerHeartbeatAt = null;
            this.showDelayedResponseNotice = false;
        },
        markChatProgress() {
            this.lastProgressAt = Date.now();
            this.lastWorkerHeartbeatAt = this.lastProgressAt;
            this.showDelayedResponseNotice = false;
        },
        updateChatProgressFromStatus(data) {
            // Repeated snapshots and heartbeats must not manufacture progress.
            const progressAt = Number(data.last_progress_at || data.phase_last_progress_at || data.started_at || 0) * 1000;
            const heartbeatAt = Number(data.last_heartbeat_at || 0) * 1000;
            if (progressAt) this.lastProgressAt = Math.max(this.lastProgressAt || 0, progressAt);
            if (heartbeatAt) this.lastWorkerHeartbeatAt = Math.max(this.lastWorkerHeartbeatAt || 0, heartbeatAt);
            this.updateDelayedResponseNotice();
        },
        updateDelayedResponseNotice() {
            const now = Date.now();
            this.showDelayedResponseNotice = !!(
                this.message.isLoading && this.isLatestBotMessage() && !this.stopping
                && this.lastWorkerHeartbeatAt
                && now - this.lastWorkerHeartbeatAt <= this.CHAT_HEARTBEAT_FRESH_SECONDS * 1000
                && now - (this.lastProgressAt || this.progressWaitStartedAt) >= 60000
            );
        },
        markChatLiveness(signalType, options = {}) {
            const {
                clearInitialGrace = true,
                restartInitialGrace = false,
                resetTimeout = true,
                stopRecoveryPolling = false,
                countsAsChatRealtimeActivity = true,
            } = options;

            this.hasReceivedRealtimeActivity = true;
            if (countsAsChatRealtimeActivity) {
                this.hasReceivedChatRealtimeActivity = true;
            }
            this.lastLivenessSignalType = signalType || 'unknown';
            this.lastLivenessAt = Date.now();
            if (signalType === 'heartbeat') {
                this.lastWorkerHeartbeatAt = Date.now();
            } else if (signalType === 'text-delta' || signalType === 'research') {
                this.markChatProgress();
            }
            this.updateDelayedResponseNotice();

            if (this.isResumedLoadingState) {
                this.clearResumeTimeout();
                this.isResumedLoadingState = false;
            }

            if (stopRecoveryPolling) {
                this.stopChatRecoveryPolling();
            }

            if (clearInitialGrace) {
                this.clearInitialUpdateGraceTimeout();
            } else if (restartInitialGrace) {
                this.restartInitialUpdateGraceTimeout();
            }

            if (resetTimeout) {
                this.resetChatUpdateTimeout();
            }
        },
        getChatLivenessSignalType(data) {
            if (!data) {
                return 'unknown';
            }
            if (this.isHeartbeatUpdate(data)) {
                return 'heartbeat';
            }
            if (data.done) {
                return 'done';
            }
            if (data.cancelled) {
                return 'cancelled';
            }
            if (data.error) {
                return 'error';
            }
            if (typeof data.text === 'string' && data.text.length > 0) {
                return 'text-delta';
            }
            return 'chat-update';
        },
        isAiFallbackTestEnabled() {
            return typeof window !== 'undefined' && window.test_ai_fallback === true;
        },
        logAiFallbackTest(event, details = {}) {
            if (!this.isAiFallbackTestEnabled()) {
                return;
            }
            if (typeof window.ensureAiFallbackTestHarness === 'function') {
                window.ensureAiFallbackTestHarness();
            }
            if (typeof window.logAiFallbackTestEvent === 'function') {
                window.logAiFallbackTestEvent('chat', event, details);
            } else {
                console.log('[AI Fallback Test][chat]', event, details);
            }
        },
        buildChatFallbackLogDetails(data = null, extra = {}) {
            const payload = data || {};
            return Object.assign({
                chat_uuid: window.chat_uuid || null,
                active_run_id: this.activeRunId || null,
                message_run_id: this.normalizeRunId(this.message && this.message.run_id),
                incoming_run_id: this.normalizeRunId(payload.run_id),
                sequence: payload.sequence !== undefined ? payload.sequence : null,
                heartbeat: !!payload.heartbeat,
                done: !!payload.done,
                cancelled: !!payload.cancelled,
                error: !!payload.error,
                local_text_length: this.message && typeof this.message.text === 'string' ? this.message.text.length : 0,
                incoming_text_length: typeof payload.text === 'string' ? payload.text.length : null,
                incoming_message_length: typeof payload.message === 'string' ? payload.message.length : null,
                suggested_questions_count: Array.isArray(payload.suggested_questions) ? payload.suggested_questions.length : 0,
                recovery_polling_active: !!this.recoveryPollingActive,
                recovery_poll_attempts: Number(this.recoveryPollAttempts || 0),
                is_loading: !!(this.message && this.message.isLoading),
                is_latest: typeof this.isLatestBotMessage === 'function' ? this.isLatestBotMessage() : null,
                resumed_loading_state: !!this.isResumedLoadingState,
                phase: payload.phase || null,
                status: payload.status || null,
                last_progress_at: payload.last_progress_at || null,
                last_heartbeat_at: payload.last_heartbeat_at || null,
                last_liveness_signal_type: this.lastLivenessSignalType || null,
                last_liveness_at: this.lastLivenessAt || null,
            }, extra || {});
        },
        getCurrentKnownRunId() {
            return this.activeRunId || this.normalizeRunId(this.message && this.message.run_id);
        },
        isPolledStatusFromCompletedRun(data) {
            const incomingRunId = this.normalizeRunId(data && data.run_id);
            const completedRunId = this.normalizeRunId(this.lastCompletedRunId);
            if (!incomingRunId || !completedRunId) return false;
            const comparison = this.compareRunIds(completedRunId, incomingRunId);
            return comparison <= 0;
        },
        ignoreCompletedPolledRun(data, source = 'check_chat_status') {
            const hasPolledText = !!(
                (typeof (data && data.message) === 'string' && data.message.length > 0)
                || (typeof (data && data.text) === 'string' && data.text.length > 0)
            );
            if (!data || !(data.done || data.cancelled || hasPolledText)) return false;
            if (!this.isPolledStatusFromCompletedRun(data)) return false;
            this.logAiFallbackTest('ignored-completed-polled-run', this.buildChatFallbackLogDetails(data, {
                source,
                last_completed_run_id: this.normalizeRunId(this.lastCompletedRunId),
            }));
            return true;
        },
        normalizeRunId(runId) {
            if (runId === undefined || runId === null || runId === '') {
                return null;
            }
            return String(runId);
        },
        compareRunIds(currentRunId, incomingRunId) {
            if (currentRunId === incomingRunId) {
                return 0;
            }

            const currentNumeric = Number(currentRunId);
            const incomingNumeric = Number(incomingRunId);
            if (Number.isFinite(currentNumeric) && Number.isFinite(incomingNumeric)) {
                return incomingNumeric > currentNumeric ? 1 : -1;
            }

            // Live chat runs use monotonically increasing numeric ids while
            // durable continuations use UUIDs.  Those namespaces have no
            // meaningful lexical ordering: a foreign UUID must never replace
            // the active live reply just because it sorts later.
            return -1;
        },
        acceptChatRunUpdate(data) {
            this.bindReservedChatRunId();
            if (isStaleInFlightChatUpdate(this.staleInFlightChatUpdateArgs(data))) {
                console.log('BotMessage: Ignoring stale in-flight chat update from a completed run');
                this.logAiFallbackTest('ignored-stale-inflight-update', this.buildChatFallbackLogDetails(data, {
                    reason: 'stale-inflight',
                    last_completed_run_id: this.normalizeRunId(this.lastCompletedRunId),
                }));
                return false;
            }
            const incomingRunId = this.normalizeRunId(data && data.run_id);
            if (!incomingRunId) {
                const incomingResponseId = durableChatResponseId(
                    data && data.response_message_id
                );
                const reservedResponseId = durableChatResponseId(
                    this.message && (
                        this.message.response_message_id || this.message.message_id
                    )
                );
                const matchesReservedResponse = !!(
                    incomingResponseId
                    && reservedResponseId
                    && incomingResponseId === reservedResponseId
                );
                if (this.activeRunId && data && (data.done || data.cancelled || data.error)) {
                    if (!matchesReservedResponse) {
                        console.log(`BotMessage: Ignoring runless terminal chat update while active run is ${this.activeRunId}`);
                        this.logAiFallbackTest('ignored-runless-terminal-update', this.buildChatFallbackLogDetails(data, {
                            reason: 'runless-terminal',
                        }));
                        return false;
                    }
                }
                this.bindAcceptedChatResponseId(data);
                return true;
            }

            if (!this.activeRunId) {
                this.activeRunId = incomingRunId;
                this.message.run_id = incomingRunId;
                this.bindAcceptedChatResponseId(data);
                this.logAiFallbackTest('active-run-initialized', this.buildChatFallbackLogDetails(data, {
                    next_active_run_id: incomingRunId,
                }));
                return true;
            }

            const comparison = this.compareRunIds(this.activeRunId, incomingRunId);
            if (comparison === 0) {
                this.message.run_id = incomingRunId;
                this.bindAcceptedChatResponseId(data);
                return true;
            }

            if (comparison < 0) {
                console.log(`BotMessage: Ignoring stale chat update for run ${incomingRunId}; active run is ${this.activeRunId}`);
                this.logAiFallbackTest('ignored-stale-run-update', this.buildChatFallbackLogDetails(data, {
                    reason: 'stale-run',
                }));
                return false;
            }

            console.log(`BotMessage: Switching active chat run from ${this.activeRunId} to ${incomingRunId}`);
            this.logAiFallbackTest('active-run-switched', this.buildChatFallbackLogDetails(data, {
                previous_active_run_id: this.activeRunId,
                next_active_run_id: incomingRunId,
            }));
            this.activeRunId = incomingRunId;
            this.message.run_id = incomingRunId;
            this.chatSequence = 0;
            this.chatChunkState = { groups: {}, completedIds: [] };
            this.message.text = '';
            this.message.cancelled = false;
            this.message.waitingForUser = false;
            this.message.isLoading = true;
            this.reasoningSeedRequested = false;
            this.hasReceivedInitialResponse = false;
            this.suppressInitialResponseDetection = false;
            this.resetRealtimeActivityState();
            this.bindAcceptedChatResponseId(data);
            return true;
        },
        handleChatUpdate(data) {
            // Final safety check - only process if we're still the latest bot message
            if (!this.isLatestBotMessage()) {
                console.log('BotMessage: Ignoring chat update in handleChatUpdate - no longer the latest bot message');
                return;
            }
            if (data && data.error && data.canonical_reconciled !== true) {
                // Runtime status is an expiring wake-up channel, not the
                // authority for whether an answer or tool effect committed.
                // Reconcile the durable chat once before exposing a retry.
                this.clearChatUpdateTimeout();
                this.clearInitialUpdateGraceTimeout();
                this.stopChatRecoveryPolling();
                this.reconcileCanonicalBeforeTerminalError(data).catch((error) => {
                    this.logAiFallbackTest('canonical-recovery-error', this.buildChatFallbackLogDetails(data, {
                        error,
                    }));
                });
                return;
            }
            if (this.message && this.message.awaitingInitialRequestAcceptance === true) {
                console.log('BotMessage: Ignoring chat update before /chat_stream accepted this turn');
                return;
            }

            // Status snapshots call this field is_complete while streamed events
            // historically use done. Normalize it once so a paused context request
            // follows the same terminal cleanup path as a completed stream.
            if (data && data.is_complete === true && !data.done) {
                data = { ...data, done: true };
            }
            const isHeartbeat = this.isHeartbeatUpdate(data);
            if (!this.acceptChatRunUpdate(data)) {
                return;
            }

            if (data.response_audio_source) this.message.response_audio_source = data.response_audio_source;

            if (!data.error) {
                this.markChatLiveness(
                    this.getChatLivenessSignalType(data),
                    {
                        clearInitialGrace: !isHeartbeat,
                        restartInitialGrace: isHeartbeat,
                        resetTimeout: !isHeartbeat,
                        stopRecoveryPolling: !isHeartbeat,
                    }
                );
            }

            // Handle error states
            if (data.error) {
                if (this.shouldIgnoreTerminalErrorUpdate(data)) {
                    this.logAiFallbackTest('ignored-terminal-error-after-answer', this.buildChatFallbackLogDetails(data, {
                        reason: 'answer-already-delivered',
                    }));
                    this.clearChatUpdateTimeout();
                    this.clearInitialUpdateGraceTimeout();
                    this.stopChatRecoveryPolling();
                    return;
                }
                if (this.isResumedLoadingState) {
                    this.clearResumeTimeout();
                    this.isResumedLoadingState = false;
                }
                console.error("Chat error received:", data.error_message);
                this.logAiFallbackTest('terminal-error', this.buildChatFallbackLogDetails(data, {
                    error_message: data.error_message || null,
                }));

                // Clear timeouts on error
                this.clearChatUpdateTimeout();
                this.clearInitialUpdateGraceTimeout();
                this.stopChatRecoveryPolling();

                // Clean up reasoning indicator on error
                if (this.isReasoningDuringChat) {
                    this.isReasoningDuringChat = false;
                    this.removeReasoningIndicator();
                }

                // Display error message
                this.suppressInitialResponseDetection = true;
                const responseMessageId = String(data && data.response_message_id || '').trim();
                const triggerMessageId = String(data && data.trigger_message_id || '').trim();
                const turnId = String(data && data.turn_id || '').trim();
                if (responseMessageId) {
                    try { this.$set(this.message, 'response_message_id', responseMessageId); } catch (e) { this.message.response_message_id = responseMessageId; }
                    try { this.$set(this.message, 'message_id', responseMessageId); } catch (e) { this.message.message_id = responseMessageId; }
                }
                if (triggerMessageId) {
                    try { this.$set(this.message, 'ai_response_anchor_message_id', triggerMessageId); } catch (e) { this.message.ai_response_anchor_message_id = triggerMessageId; }
                }
                if (turnId) {
                    try { this.$set(this.message, 'turn_id', turnId); } catch (e) { this.message.turn_id = turnId; }
                }
                this.message.text = data.error_message || "Ocurrió un error. Por favor, intente de nuevo.";
                this.message.isLoading = false;
                this.message.cancelled = false;
                this.message.waitingForUser = false;
                this.chatChunkState = { groups: {}, completedIds: [] };
                try { this.$set(this.message, 'failed', true); } catch (e) { this.message.failed = true; }
                try { this.$set(this.message, 'retryable', data.recoverable !== false); } catch (e) { this.message.retryable = data.recoverable !== false; }

                // Stop listening to updates
                this.unsubscribeFromResearchActivity();
                this.unsubscribeFromChatUpdates();

                // Emit error event to parent
                this.$emit('messageError', this.message);

                return;
            }

            // Reset timeout on each successful update
            if (!isHeartbeat) {
                this.logAiFallbackTest(data.cancelled
                    ? 'terminal-cancelled'
                    : (data.done ? 'terminal-done' : 'update-applied'), this.buildChatFallbackLogDetails(data));
            }

            // If we're getting chat updates, reasoning during chat is done
            if (this.isReasoningDuringChat) {
                this.isReasoningDuringChat = false;
                this.removeReasoningIndicator();
            }

            if (isHeartbeat) {
                return;
            }

            const chunkResult = consumeChunkedRealtimeUpdate(
                this.chatChunkState,
                data,
                this.chatSequence,
                'text'
            );
            if (chunkResult.status === 'pending' || chunkResult.status === 'duplicate') {
                return;
            }
            if (chunkResult.status === 'gap' || chunkResult.status === 'invalid') {
                this.startChatRecoveryPolling('chunk-gap');
                return;
            }
            data = chunkResult.data;

            if (data.waiting_for_user === true || data.waitingForUser === true || data.phase === 'waiting_user' || data.status === 'waiting_user') {
                const responseMessageId = String(data && data.response_message_id || '').trim();
                if (responseMessageId) {
                    // A paused response has a server-reserved id. Preserve it
                    // on this exact placeholder so the parent can reconcile a
                    // durable context receipt even if the tool event arrived
                    // before this realtime wait update.
                    try { this.$set(this.message, 'response_message_id', responseMessageId); } catch (e) { this.message.response_message_id = responseMessageId; }
                    try { this.$set(this.message, 'message_id', responseMessageId); } catch (e) { this.message.message_id = responseMessageId; }
                }
                const runId = String(data && data.run_id || '').trim();
                if (runId) {
                    try { this.$set(this.message, 'run_id', runId); } catch (e) { this.message.run_id = runId; }
                }
                this.message.waitingForUser = true;
                this.message.cancelled = false;
                if (typeof data.text === 'string') {
                    this.message.text = data.text;
                }
                this.message.isLoading = false;
                this.clearChatUpdateTimeout();
                this.clearInitialUpdateGraceTimeout();
                this.stopChatRecoveryPolling();
                this.unsubscribeFromResearchActivity();
                this.unsubscribeFromChatUpdates();
                this.chatSequence = 0;
                this.chatChunkState = { groups: {}, completedIds: [] };
                this.$emit('messageWaitingForContext', this.message);
                return;
            }

            // Sequence check
            if (data.sequence !== undefined) {
                if (hasChatSequenceGap(this.chatSequence, data)) {
                    console.error(`Out of order chat update. Expected ${this.chatSequence}, got ${data.sequence}. Ignoring.`);
                    this.startChatRecoveryPolling('sequence-gap');
                    return;
                }
                this.chatSequence++;
            }

            // Update message text: replace with authoritative full text when requested.
            if (data.replace && typeof data.text === 'string') {
                this.message.text = data.text;
            } else if (data.done && typeof data.text === 'string' && data.text.length > 0) {
                this.message.text = data.text;
            } else if (data.text !== undefined && data.text !== "") {
                this.message.text = this.message.text + data.text;
            }
            if (data.text !== undefined && data.text !== '') {
                this.message.waitingForUser = false;
            }
            this.maybeHandleFirstVisibleAnswerText();

            // Update completion status
            if (data.cancelled) {
                // Treat as done but without saving or suggesting questions
                this.message.isLoading = false;
                this.message.cancelled = true;
                this.clearChatUpdateTimeout();
                this.clearInitialUpdateGraceTimeout();
                this.stopChatRecoveryPolling();
                this.unsubscribeFromResearchActivity();
                this.unsubscribeFromChatUpdates();
                this.chatSequence = 0;
                this.chatChunkState = { groups: {}, completedIds: [] };
                this.$emit('messageCompleted', this.message);
                return;
            }

            if (data.done) {
                this.message.isLoading = false;

                // Clean up reasoning indicator on completion
                if (this.isReasoningDuringChat) {
                    this.isReasoningDuringChat = false;
                    this.removeReasoningIndicator();
                }

                // Handle suggested questions
                if (data.suggested_questions) {
                    this.$emit('suggestedQuestionsReceived', data.suggested_questions);
                }

                // Clear timeouts on completion
                this.clearChatUpdateTimeout();
                this.clearInitialUpdateGraceTimeout();
                this.stopChatRecoveryPolling();

                // Stop listening to updates
                this.unsubscribeFromResearchActivity();
                this.unsubscribeFromChatUpdates();

                // Reset sequence for next message
                this.chatSequence = 0;
                this.chatChunkState = { groups: {}, completedIds: [] };

                // Emit completion event
                this.$emit('messageCompleted', this.message);
            }
        },
        async stopGeneration() {
            if (!this.isLatestBotMessage() || !this.message.isLoading) return;
            if (!this.canStopNow) return;
            // Ask for user confirmation before stopping the generation
            const userConfirmed = window.confirm('¿Deseas detener la generación? Esto cancelará la respuesta actual. Luego podrás editar tu último mensaje.');
            if (!userConfirmed) {
                return;
            }
            // Re-validate immediately after confirmation to ensure it's still cancellable
            if (!this.isLatestBotMessage() || !this.message.isLoading || !this.canStopNow) {
                alert('Ya no es posible detener la generación de la respuesta.');
                return;
            }
            try {
                this.stopping = true;
                const resp = await fetch('/cancel_chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ uuid: window.chat_uuid }) });
                if (!resp.ok) {
                    throw new Error('Error al enviar la solicitud de cancelación');
                }
                // Optimistic UI state until Ably confirms
                this.message.cancelled = true;
                this.message.isLoading = false;
                this.$emit('messageCompleted', this.message);
            } catch (e) {
                console.error('Error cancelling chat:', e);
                alert('No se pudo cancelar la generación. Por favor, intente nuevamente.');
            } finally {
                this.stopping = false;
            }
        },
        // Relay actions to parent so it can handle retry/edit
        retryAfterCancel() {
            this.$emit('retryAfterCancel', this.message);
        },
        editLastUser() {
            this.$emit('editLastUser', this.message);
        },
        addToResearchHistory(activity) {
            if (!activity || !activity.message || !this.researchActivityMatchesCurrentRun(activity)) return;

            // Initialize research activity history if it doesn't exist
            if (!this.message.researchActivityHistory) {
                this.message.researchActivityHistory = [];
            }

            const activitySeq = this.researchActivitySeq(activity);
            if (activitySeq) {
                if (activitySeq <= this.lastResearchActivitySeq) return;
                if (activitySeq > this.lastResearchActivitySeq + 1) {
                    // Keep displaying the newest live step, but reconcile the
                    // missing durable range in the background.
                    this.recoverResearchActivityGap(
                        this.lastResearchActivitySeq + 1,
                        activitySeq - 1,
                    );
                }
                this.lastResearchActivitySeq = activitySeq;
            }

            // Check if this activity is different from the last one
            const lastActivity = this.message.researchActivityHistory[this.message.researchActivityHistory.length - 1];
            const incomingKey = this.researchActivityDedupeKey(activity);
            if (!lastActivity || this.researchActivityDedupeKey(lastActivity) !== incomingKey) {

                this.addResearchActivityToHistory(activity);
            }
        },
        // Helper function to show reasoning indicator during chat writing
        showReasoningIndicator() {
            // Clear any existing timeout
            if (this.reasoningIndicatorTimeout) {
                clearTimeout(this.reasoningIndicatorTimeout);
            }

            // Wait for DOM updates to complete
            this.reasoningIndicatorTimeout = setTimeout(() => {
                const botResponseEl = this.$el.querySelector('.bot-response');
                if (!botResponseEl) return;

                // Only add if not already present
                if (!botResponseEl.querySelector('.reasoning-during-chat-indicator')) {
                    const indicator = document.createElement('span');
                    indicator.className = 'reasoning-during-chat-indicator';
                    indicator.innerHTML = ' <i class="anticon anticon-bulb anticon-spin" style="color: var(--theme-color); font-size: 1.2em; margin-left: 5px;"></i>';
                    indicator.title = 'La IA está razonando...';

                    // Add at the end of the current content
                    botResponseEl.appendChild(indicator);
                }
            }, 200); // Delay to ensure text is rendered
        },

        // Helper function to remove reasoning indicator
        removeReasoningIndicator() {
            if (this.reasoningIndicatorTimeout) {
                clearTimeout(this.reasoningIndicatorTimeout);
                this.reasoningIndicatorTimeout = null;
            }

            const indicators = this.$el.querySelectorAll('.reasoning-during-chat-indicator');
            indicators.forEach(indicator => {
                indicator.remove();
            });
        },

        triggerNewActivityAnimation() {
            this.$nextTick(() => {
                const historySection = this.$el.querySelector('.research-history-section');
                if (historySection) {
                    historySection.classList.add('new-activity');
                    setTimeout(() => {
                        historySection.classList.remove('new-activity');
                    }, 600);
                }
            });
        },

        // Helper function to translate activity status to Spanish
        translateStatus(status) {
            if (status == "searching") {
                return "Buscando";
            } else if (status == "reasoning") {
                return "Razonando";
            }

            return status;
        },
        forceUnsubscribeIfNotLatest() {
            // Safety check to ensure method exists and component is ready
            if (typeof this.isLatestBotMessage !== 'function') {
                console.log('BotMessage: isLatestBotMessage method not ready yet');
                return;
            }

            try {
                if (!this.isLatestBotMessage()) {
                    this.stopSlowToolProgress();
                }
                if (!this.isLatestBotMessage() && (this.ablyChannel || this.chatChannel)) {
                    console.log('BotMessage: Force unsubscribing - no longer the latest bot message');
                    this.unsubscribeFromResearchActivity();
                    this.unsubscribeFromChatUpdates();
                    this.syncAblyConnectionHandlers();

                    // Also clear any loading states if we're not the latest
                    if (this.isReasoningDuringChat) {
                        this.isReasoningDuringChat = false;
                        this.removeReasoningIndicator();
                    }
                }
            } catch (error) {
                console.error('BotMessage: Error in forceUnsubscribeIfNotLatest:', error);
            }
        },
        shouldOwnAblyConnectionHandlers() {
            return !!(
                this.isLatestBotMessage()
                && (
                    this.message.isLoading === true
                    || (
                        this.message.waitingForUser === true
                        && this.message.resumingContext === true
                    )
                )
            );
        },
        registerAblyConnectionHandlers() {
            if (this.ablyConnectionHandlers || !this.shouldOwnAblyConnectionHandlers()) return;
            const client = window.ably_realtime_client;
            const connection = client && client.connection;
            if (!connection || typeof connection.on !== 'function') return;

            const connected = () => {
                console.log('BotMessage: Ably connection restored');
                this.stopChatRecoveryPolling();

                // Rehydrate progress after reconnecting. Ably recovers brief
                // disconnects, while the durable status endpoint covers any
                // activity outside the recovery window.
                if (this.shouldReceiveResearchActivity() && window.chat_uuid && this.isLatestBotMessage()) {
                    console.log('BotMessage: Resubscribing to channels after reconnection');
                    this.seedResearchActivityHistoryFromServer({
                        reasoningOnly: this.message.resumingContext === true,
                        force: true,
                    });
                    this.subscribeToResearchActivity();
                    if (this.message.isLoading) {
                        this.subscribeToChatUpdates();
                    }
                }
            };
            const failed = (error) => {
                console.error('BotMessage: Ably connection failed:', error);
                if (this.message.isLoading && this.isLatestBotMessage()) {
                    this.startChatRecoveryPolling('connection-failed');
                }
            };
            const disconnected = () => {
                if (this.message.isLoading && this.isLatestBotMessage()) {
                    this.startChatRecoveryPolling('connection-disconnected');
                }
            };
            const suspended = () => {
                if (this.message.isLoading && this.isLatestBotMessage()) {
                    this.startChatRecoveryPolling('connection-suspended');
                }
            };

            connection.on('connected', connected);
            connection.on('failed', failed);
            connection.on('disconnected', disconnected);
            connection.on('suspended', suspended);
            this.ablyConnectionHandlers = {
                connection,
                connected,
                failed,
                disconnected,
                suspended,
            };
        },
        unregisterAblyConnectionHandlers() {
            const handlers = this.ablyConnectionHandlers;
            if (!handlers) return;
            const connection = handlers.connection;
            if (connection && typeof connection.off === 'function') {
                connection.off('connected', handlers.connected);
                connection.off('failed', handlers.failed);
                connection.off('disconnected', handlers.disconnected);
                connection.off('suspended', handlers.suspended);
            }
            this.ablyConnectionHandlers = null;
        },
        syncAblyConnectionHandlers() {
            if (this.shouldOwnAblyConnectionHandlers()) {
                this.registerAblyConnectionHandlers();
            } else {
                this.unregisterAblyConnectionHandlers();
            }
        },
    },
    mounted() {
        this._documentResponseLayoutKey = this.hasDocumentOutput ? this.documentResponseLayoutKey : null;
        // Initialize research activity history on mount
        this.ensureResearchActivityHistory();
        if (this.hasResearchHistory) {
            this.selectLatestResearchEntry();
        }
        this.lastResearchActivitySeq = this.message.researchActivityHistory.reduce((highest, activity) => (
            Math.max(highest, this.researchActivitySeq(activity))
        ), 0);
        this.activeRunId = this.normalizeRunId(this.message && this.message.run_id);
        this.syncSlowToolProgress();

        this.$nextTick(() => {
            this.markRenderedResponseViewportAnchors();
            this.attachLinkHoverEvents();
            this.attachSourcesGroupTriggerEvents();
            this.attachLexiusDocButtons();
            this.updateLexiusDocCardTitles();
            this.mountResponseAudio();

            // Keep the same function reference for Vue 3 unmount cleanup.
            this.boundCloseDetailsHandler = () => this.hideDocDetailsPopup();
            window.addEventListener('close-details-popup', this.boundCloseDetailsHandler);

            // Use setTimeout to ensure parent components are fully ready
            setTimeout(() => {
                // Check if this message is loading and is the latest - set up subscriptions
                if (
                    this.message.isLoading
                    && this.isLatestBotMessage()
                ) {
                    console.log('BotMessage: Message is loading and is the latest');
                    this.subscribeToResearchActivity();
                    if (this.message.awaitingInitialRequestAcceptance !== true) {
                        this.seedResearchActivityHistoryFromServer();
                        this.subscribeToChatUpdates();
                    }
                    this.maybeHandleFirstVisibleAnswerText();
                } else if (
                    this.message.waitingForUser === true
                    && this.message.resumingContext === true
                    && this.isLatestBotMessage()
                ) {
                    this.startContinuationReasoningRecovery();
                    this.subscribeToResearchActivity();
                }
            }, 50);

            // Periodically check if we're still the latest message (start after a delay)
            setTimeout(() => {
                this.latestCheckInterval = setInterval(() => {
                    this.forceUnsubscribeIfNotLatest();
                    this.updateDelayedResponseNotice();
                }, 1000);
            }, 100);

            this.syncAblyConnectionHandlers();
        });
    },
    beforeUpdate() {
        // Capture before the answer replaces its placeholder or research folds.
        // Restore in the same render cycle so the document never visibly jumps.
        const layoutKey = this.hasDocumentOutput ? this.documentResponseLayoutKey : null;
        this._documentResponseViewport = layoutKey && layoutKey !== this._documentResponseLayoutKey
            ? this.captureDocumentResponseViewport?.() : null;
        this._documentResponseLayoutKey = layoutKey;
    },
    updated() {
        if (this._documentResponseViewport) {
            this.restoreDocumentResponseViewport?.(this._documentResponseViewport);
            this._documentResponseViewport = null;
        }
        this.$nextTick(() => {
            this.markRenderedResponseViewportAnchors();
            this.attachLinkHoverEvents();
            this.attachSourcesGroupTriggerEvents();
            this.attachLexiusDocButtons();
            this.updateLexiusDocCardTitles();
            this.mountResponseAudio();
        });
    },
    beforeUnmount() {
        clearTimeout(this.documentResponseReadyTimer);
        // Remove the event listeners when component is destroyed
        if (this.boundCloseDetailsHandler) {
            window.removeEventListener('close-details-popup', this.boundCloseDetailsHandler);
            this.boundCloseDetailsHandler = null;
        }
        this.unsubscribeFromResearchActivity();
        this.stopSlowToolProgress();
        this.unsubscribeFromChatUpdates();
        this.stopContinuationReasoningRecovery();
        this.clearInitialUpdateGraceTimeout();
        this.stopChatRecoveryPolling();
        this.detachLexiusDocButtons();
        this.unregisterAblyConnectionHandlers();

        // Clean up reasoning indicator state
        this.isReasoningDuringChat = false;
        this.removeReasoningIndicator();

        // Clear the resume timeout
        this.clearResumeTimeout();

        // Clear the interval
        if (this.latestCheckInterval) {
            clearInterval(this.latestCheckInterval);
            this.latestCheckInterval = null;
        }
    },
    watch: {
        liveDocumentResponsePending: {
            immediate: true,
            handler(pending) {
                // Previews can arrive after a tool has already made the response pending.
                if (pending) this.documentResponseHadLivePreview = true;
            },
        },
        documentResponseState: {
            immediate: true,
            handler(state) {
                if (state !== 'ready') {
                    clearTimeout(this.documentResponseReadyTimer);
                    this.documentResponseReadyTimer = null;
                    this.documentResponseCompletedHere = false;
                }
                if (state === 'pending') {
                    if (!this.documentResponseWasPending) {
                        this.documentResponseHadLivePreview = this.hasLiveDocumentPreview;
                    }
                    this.documentResponseWasPending = true;
                } else if (state === 'ready' && this.documentResponseWasPending) {
                    this.documentResponseWasPending = false;
                    this.documentResponseCompletedHere = true;
                    this.documentResponseReadyTimer = setTimeout(() => {
                        this.documentResponseCompletedHere = false;
                        this.documentResponseReadyTimer = null;
                    }, 3000);
                    // A review or a saved artifact alone did not give the reader a live document.
                    if (this.documentResponseHadLivePreview) {
                        this.$nextTick(() => this.$emit('documentResponseReady', this.message));
                    }
                    this.documentResponseHadLivePreview = false;
                } else if (this.message.cancelled || this.message.failed || this.message.waitingForUser) {
                    this.documentResponseWasPending = false;
                    this.documentResponseHadLivePreview = false;
                }
            },
        },
        'message.text': {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    this.stopSlowToolProgress();
                    this.maybeHandleFirstVisibleAnswerText();
                }
            },
            immediate: true
        },
        'message.isLoading': {
            handler(newVal) {
                // Completion is the terminal UI state for this response. This
                // also keeps every restored response collapsed when a chat
                // contains more than one research timeline.
                if (newVal) {
                    this.showResearchHistory = true;
                } else if (!(
                    this.message.waitingForUser === true
                    && this.message.resumingContext === true
                )) {
                    this.showResearchHistory = false;
                }
                if (newVal && this.isLatestBotMessage()) {
                    this.activeRunId = this.normalizeRunId(this.message && this.message.run_id);
                    this.reasoningSeedRequested = false;
                    this.hasReceivedInitialResponse = false;
                    this.suppressInitialResponseDetection = false;
                    this.resetRealtimeActivityState();
                    // The backend starts its worker before /chat_stream returns
                    // the accepted run identity. Subscribe to research now so
                    // its first reasoning/search events are not lost in that
                    // acceptance window. Chat text and status recovery remain
                    // gated until the request has actually been accepted.
                    this.subscribeToResearchActivity();
                    if (this.message.awaitingInitialRequestAcceptance !== true) {
                        this.seedResearchActivityHistoryFromServer();
                        this.subscribeToChatUpdates();
                    }
                    this.maybeHandleFirstVisibleAnswerText();
                    this.$nextTick(() => this.syncSlowToolProgress());
                } else {
                    if (this.isSlowToolProgressRunActive) {
                        this.$nextTick(() => this.syncSlowToolProgress());
                    } else {
                        this.stopSlowToolProgress();
                    }
                    if (!newVal) {
                        this.activeRunId = this.normalizeRunId(this.message && this.message.run_id);
                    }
                    if (!this.shouldReceiveResearchActivity()) {
                        this.unsubscribeFromResearchActivity();
                    }
                    this.unsubscribeFromChatUpdates();

                    // Clean up reasoning indicator when message stops loading
                    if (this.isReasoningDuringChat) {
                        this.isReasoningDuringChat = false;
                        this.removeReasoningIndicator();
                    }
                }
                this.syncAblyConnectionHandlers();
            },
            immediate: false
        },
        'message.resumingContext': {
            handler(newVal) {
                if (
                    newVal === true
                    && this.message.waitingForUser === true
                    && this.isLatestBotMessage()
                ) {
                    this.showResearchHistory = true;
                    this.startContinuationReasoningRecovery();
                    this.subscribeToResearchActivity();
                    this.$nextTick(() => this.syncSlowToolProgress());
                } else if (newVal !== true && this.message.isLoading !== true) {
                    this.showResearchHistory = false;
                    this.stopContinuationReasoningRecovery();
                    this.unsubscribeFromResearchActivity();
                    this.stopSlowToolProgress();
                }
                this.syncAblyConnectionHandlers();
            },
            immediate: false
        },
        'message.waitingForUser': {
            handler() {
                this.$nextTick(() => this.syncSlowToolProgress());
                this.syncAblyConnectionHandlers();
            },
            immediate: false
        },
        'message.run_id': {
            handler() {
                if (!this.message || this.message.isLoading !== true) return;
                this.bindReservedChatRunId();
                this.$nextTick(() => this.syncSlowToolProgress());
            },
            immediate: false
        },
        'message.awaitingInitialRequestAcceptance': {
            handler(newVal, oldVal) {
                if (
                    oldVal === true
                    && newVal === false
                    && this.message.isLoading
                    && this.isLatestBotMessage()
                ) {
                    this.reasoningSeedRequested = false;
                    this.seedResearchActivityHistoryFromServer();
                    this.subscribeToResearchActivity();
                    this.subscribeToChatUpdates();
                    if (!this.recoveryPollingActive) {
                        this.resetChatUpdateTimeout();
                        if (!this.hasReceivedInitialResponse) {
                            this.scheduleInitialUpdateGraceTimeout();
                        }
                    }
                }
            },
            immediate: false
        },
        'researchHistoryEntryIds': {
            handler(newVal, oldVal) {
                // IDs, rather than positions, are the timeline's state. This
                // watcher also runs when a receipt/activity is backfilled with
                // an earlier timestamp and the sorted order changes.
                this.reconcileResearchSelection();
                this.$nextTick(() => this.syncSlowToolProgress());
                const countFromIds = value => String(value || '')
                    .split('\u001f')
                    .filter(Boolean)
                    .length;
                const newCount = countFromIds(newVal);
                const oldCount = countFromIds(oldVal);
                if (newCount > oldCount && oldVal !== undefined) {
                    // Trigger animation for count update.
                    this.$nextTick(() => {
                        const countElement = this.$el.querySelector('.research-history-count');
                        if (countElement) {
                            countElement.style.animation = 'none';
                            setTimeout(() => {
                                countElement.style.animation = 'countUpdate 0.3s ease-out';
                            }, 10);
                        }
                    });
                }
            }
        },
        followLatest: {
            handler(newVal) {
                if (!newVal) {
                    this.stopSlowToolProgress();
                    return;
                }
                this.$nextTick(() => this.syncSlowToolProgress());
            },
            immediate: false
        }
    },
    /*html*/
    template: `
        <div :data-tour="!message.isLoading && !message.cancelled && !message.waitingForUser ? 'chat-response' : null">
            <transition name="chat-artifact-action-warning">
                <aside
                    v-if="hasDocumentArtifact && artifactActionWarningVisible"
                    class="chat-artifact-action-warning"
                    data-testid="chat-artifact-action-warning"
                    role="status"
                    aria-live="polite"
                >
                    <span class="chat-artifact-action-warning__icon" aria-hidden="true">
                        <i class="anticon anticon-exclamation-circle"></i>
                    </span>
                    <span class="chat-artifact-action-warning__copy">
                        <strong>El documento tiene sus propios controles</strong>
                        <span>Usa los botones del documento para copiar o descargar su contenido. Los botones generales de esta respuesta solo incluyen la explicación.</span>
                    </span>
                    <button
                        type="button"
                        class="chat-artifact-action-warning__close"
                        @click="dismissArtifactActionWarning"
                        aria-label="Cerrar aviso sobre los controles del documento"
                        title="Cerrar aviso"
                    ><i class="anticon anticon-close" aria-hidden="true"></i></button>
                </aside>
            </transition>
            <div
                class="flex justify-end gap-2 text-md text-third pr-1 !pb-1"
                data-tour="chat-response-actions"
            >
                <template v-if="!message.isLoading && !message.waitingForUser">
                <span ref="responseAudioActions" class="response-audio-slot"></span>
                <button v-if="canReply"
                        @click="replyToMessage"
                        class="hover:text-theme-color"
                        data-testid="collaboration-reply-bot"
                        title="Responder">
                    <i class="anticon anticon-message"></i>
                </button>
                <button @click="copyMessageToClipboard" 
                        :class="{ 'hover:text-theme-color': !copyInProgress, 'copy-feedback': copyFeedback }"
                        :disabled="copyInProgress"
                        title="Copiar mensaje">
                    <i :class="copyInProgress ? 'anticon anticon-loading anticon-spin' : 'anticon anticon-copy'"></i>
                </button>
                <span v-if="copyError" class="text-xs text-red-600" role="status" aria-live="polite">{{ copyError }}</span>
                <button @click="downloadMessageAsPDF"
                        :class="{ 'hover:text-theme-color': !downloadFeedback, 'copy-feedback': downloadFeedback }"
                        title="Descargar PDF">
                    <i :class="downloadFeedback ? 'anticon anticon-loading anticon-spin' : 'anticon anticon-file-pdf'"></i>
                </button>
                <button @click="downloadMessageAsDOCX"
                        :class="{ 'hover:text-theme-color': !downloadDocxFeedback, 'copy-feedback': downloadDocxFeedback }"
                        title="Descargar Word">
                    <i :class="downloadDocxFeedback ? 'anticon anticon-loading anticon-spin' : 'anticon anticon-file-word'"></i>
                </button>
                <button @click="showMindMap"
                        class="hover:text-theme-color"
                        title="Ver mapa mental"
                        v-if="!is_call_log">
                    <i class="anticon anticon-cluster"></i>
                </button>
                </template>
            </div>

            <!-- Research Activity History Section -->
            <div v-if="hasResearchHistory" class="research-history-section mb-3" data-tour="chat-response-research">
                <div class="research-history-header">
                    <button 
                        @click="toggleResearchHistory"
                        class="research-history-toggle"
                        :class="{ 'active': showResearchHistory }"
                        :aria-expanded="showResearchHistory ? 'true' : 'false'"
                        :aria-controls="researchHistoryPanelId"
                        title="Ver historial de investigación">
                        <i class="anticon anticon-experiment"></i>
                        <span class="research-history-title">Investigación y Razonamiento</span>
                        <span class="research-history-count">({{ researchHistoryCount }})</span>
                        <i class="anticon anticon-down collapse-icon" :class="{ 'rotated': showResearchHistory }"></i>
                    </button>
                </div>
                
                <div v-if="showResearchHistory" :id="researchHistoryPanelId" class="research-history-content">
                    <div class="research-history-navigator">
                        <button 
                            @click="navigateResearchHistory('previous')"
                            :disabled="!canNavigatePrevious"
                            class="nav-btn nav-btn-prev"
                            aria-label="Ver paso anterior"
                            title="Anterior">
                            <i class="anticon anticon-left"></i>
                        </button>
                        
                        <div class="research-history-indicators">
                            <button
                                v-for="(entry, index) in researchHistoryItems"
                                :key="entry.id"
                                type="button"
                                @click="navigateToResearchEntry(entry.id)"
                                class="history-indicator"
                                :class="{ 
                                    'active': entry.id === selectedResearchEntryId,
                                    'searching': entry.status === 'searching',
                                    'reasoning': entry.status === 'reasoning',
                                    'source': entry.kind === 'source'
                                }"
                                :aria-label="researchEntryLabel(entry, index)"
                                :aria-current="entry.id === selectedResearchEntryId ? 'step' : null"
                                :title="researchEntryLabel(entry, index)"></button>
                        </div>
                        
                        <button 
                            @click="navigateResearchHistory('next')"
                            :disabled="!canNavigateNext"
                            class="nav-btn nav-btn-next"
                            aria-label="Ver paso siguiente"
                            title="Siguiente">
                            <i class="anticon anticon-right"></i>
                        </button>
                    </div>
                    
                    <div v-if="currentResearchEntry" class="research-activity-display" :data-message-viewport-ref="'research-entry:' + currentResearchEntry.id" :class="[
                        { 'research-activity-display--source': currentSourceReceipt },
                        currentSourceReceipt ? currentSourceToneClass : null
                    ]">
                        <div class="research-activity-header">
                            <div v-if="currentSourceReceipt" class="activity-type source">
                                <i class="anticon anticon-file-search"></i>
                                <span>{{ currentSourceTitle }}</span>
                            </div>
                            <div v-else class="activity-type" :class="currentResearchActivity.status">
                                <i :class="currentResearchActivity.status === 'searching' ? 'anticon anticon-search' : 'anticon anticon-bulb'"></i>
                                <span>{{ translateStatus(currentResearchActivity.status) }}</span>
                            </div>
                            <div v-if="currentSourceReceipt" class="activity-timestamp research-source-status">
                                {{ currentSourceStatusLabel }}
                            </div>
                            <div v-else class="activity-timestamp">
                                {{ new Date(currentResearchActivity.timestamp).toLocaleTimeString() }}
                            </div>
                        </div>
                        <template v-if="currentSourceReceipt">
                            <p v-if="currentSourceSummary" class="research-source-summary" :data-message-viewport-ref="'source-content:' + currentResearchEntry.id">{{ currentSourceSummary }}</p>
                            <dl v-if="currentSourceStats.length" class="research-source-stats" :data-message-viewport-ref="'source-stats:' + currentResearchEntry.id" aria-label="Resumen de las fuentes consultadas">
                                <div v-for="stat in currentSourceStats" :key="stat.label" :class="'is-' + stat.tone">
                                    <dd>{{ stat.value }}</dd>
                                    <dt>{{ stat.label }}</dt>
                                </div>
                            </dl>
                            <ul v-if="currentSourceWarnings.length" class="research-source-warnings">
                                <li v-for="(warning, index) in currentSourceWarnings" :key="index">{{ warning }}</li>
                            </ul>
                            <div v-if="currentSourceActions.length" class="research-source-actions">
                                <button
                                    v-for="action in currentSourceActions"
                                    :key="action"
                                    type="button"
                                    class="research-source-action"
                                    @click="emitSourceReceiptAction(action)"
                                ><span>{{ displaySourceAction(action) }}</span><i v-if="normalizeSourceAction(action) === 'abrir pasajes'" class="anticon anticon-arrow-right" aria-hidden="true"></i></button>
                            </div>
                        </template>
                        <div
                            v-else
                            class="research-activity-message"
                            :data-message-viewport-ref="'research-content:' + currentResearchEntry.id"
                            v-html="processedCurrentResearchActivity"
                        ></div>
                        <span
                            class="visually-hidden slow-tool-progress-status"
                            role="status"
                            aria-live="polite"
                            aria-atomic="true"
                        >{{ showDelayedResponseNotice ? '' : slowToolProgressMessage }}</span>
                    </div>
                </div>
            </div>

            <slot name="after-research"></slot>

            <div
                class="flex gap-3 items-start !p-5 !py-7 bg-fourth paragraph-text rounded-xl shrink-0 dynamic-color"
                data-tour="chat-response-content"
                :aria-busy="documentResponseState === 'pending'"
                tabindex="-1"
            >
                <div class="w-[40px] h-[40px] rounded-md overflow-hidden flex items-center justify-center bg-primary shrink-0 text-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-robot" viewBox="0 0 16 16">
                        <path d="M6 12.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5ZM3 8.062C3 6.76 4.235 5.765 5.53 5.886a26.58 26.58 0 0 0 4.94 0C11.765 5.765 13 6.76 13 8.062v1.157a.933.933 0 0 1-.765.935c-.845.147-2.34.346-4.235.346-1.895 0-3.39-.2-4.235-.346A.933.933 0 0 1 3 9.219V8.062Zm4.542-.827a.25.25 0 0 0-.217.068l-.92.9a24.767 24.767 0 0 1-1.871-.183.25.25 0 0 0-.068.495c.55.076 1.232.149 2.02.193a.25.25 0 0 0 .189-.071l.754-.736.847 1.71a.25.25 0 0 0 .404.062l.932-.97a25.286 25.286 0 0 0 1.922-.188.25.25 0 0 0-.068-.495c-.538.074-1.207.145-1.98.189a.25.25 0 0 0-.166.076l-.754.785-.842-1.7a.25.25 0 0 0-.182-.135Z"></path>
                        <path d="M8.5 1.866a1 1 0 1 0-1 0V3h-2A4.5 4.5 0 0 0 1 7.5V8a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1v1a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-1a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1v-.5A4.5 4.5 0 0 0 10.5 3h-2V1.866ZM14 7.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7.5A3.5 3.5 0 0 1 5.5 4h5A3.5 3.5 0 0 1 14 7.5Z"></path>
                    </svg>
                </div>
                <div class="w-5/6 leading-7 bot-response">
                    <div
                        v-if="messageDateTime"
                        class="collaboration-message-time"
                        data-testid="collaboration-message-time"
                    >{{ messageDateTime }}</div>
                    <button
                        v-if="replyPreview"
                        type="button"
                        class="collaboration-reply-reference"
                        data-testid="collaboration-reply-reference"
                        :title="'Ir al mensaje citado de ' + replyPreview.sender_label"
                        @click="navigateToReply"
                    >
                        <span class="collaboration-reply-reference__meta">{{ replyPreview.sender_label }}</span>
                        <span class="collaboration-reply-reference__snippet">{{ replyPreview.snippet }}</span>
                    </button>
                    <div v-if="message.isLoading && showDelayedResponseNotice"
                        class="text-muted" role="status" aria-live="polite"
                        data-testid="chat-delayed-response-notice">
                        <p>La respuesta está tardando más de lo habitual. Puedes esperar o cancelar.</p>
                        <button v-if="message.text.length > 0" class="stop-generate-btn"
                            :disabled="stopping" @click="stopGeneration" title="Detener generación">
                            <i class="anticon anticon-stop"></i> Detener
                        </button>
                    </div>
                    <div v-if="documentResponseState === 'pending' || (message.isLoading && message.text.length === 0)" class="loading-container">
                        <div class="loading-message">
                            <span v-if="documentResponseState === 'pending'"
                                class="document-response-placeholder" data-testid="document-response-placeholder"
                                role="status">Preparando la respuesta final…</span>
                            <div v-else class="loading-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                        <div class="loading-actions">
                            <button class="stop-generate-btn" :disabled="!canStopNow || stopping" @click="stopGeneration" title="Detener generación">
                                <i :class="stopping ? 'anticon anticon-loading anticon-spin' : 'anticon anticon-stop'"></i>
                                <span>Detener</span>
                            </button>
                        </div>
                    </div>
                    <div v-else-if="message.cancelled" class="cancelled-notice">
                        <div class="cancelled-icon"><i class="anticon anticon-stop"></i></div>
                        <div class="cancelled-copy">
                            <strong>Generación cancelada</strong>
                            <p>{{ terminalTriggerIsRunningQueuedAi()
                                ? 'La generación se detuvo antes de completarse. Puedes reintentar esta solicitud en cola.'
                                : 'La generación se detuvo antes de completarse. Puedes reintentar o editar tu último mensaje.'
                            }}</p>
                            <div class="cancelled-actions">
                                <button 
                                    v-if="ownsTerminalActions()"
                                    class="btn btn-sm retry-btn" 
                                    @click="retryAfterCancel" 
                                    title="Volver a intentarlo">
                                    <i class="anticon anticon-reload"></i> Reintentar
                                </button>
                                <button 
                                    v-if="ownsTerminalActions() && !terminalTriggerIsRunningQueuedAi()"
                                    class="btn btn-sm edit-btn" 
                                    @click="editLastUser" 
                                    title="Editar último mensaje">
                                    <i class="anticon anticon-edit"></i> Editar mensaje
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="message.failed" class="cancelled-notice terminal-error-notice" role="alert">
                        <div class="cancelled-icon"><i class="anticon anticon-warning"></i></div>
                        <div class="cancelled-copy">
                            <strong>No se pudo completar la respuesta</strong>
                            <p>{{ message.retryable !== false
                                ? 'No pudimos completar la respuesta. Tu mensaje y los documentos del chat se conservan.'
                                    + (ownsTerminalActions() ? ' Pulsa «Reintentar».' : '')
                                : message.text }}</p>
                            <div class="cancelled-actions">
                                <button
                                    v-if="message.retryable !== false && ownsTerminalActions()"
                                    class="btn btn-sm retry-btn"
                                    @click="retryAfterCancel"
                                    title="Volver a intentarlo"
                                >
                                    <i class="anticon anticon-reload"></i> Reintentar
                                </button>
                                <button
                                    v-if="message.retryable !== false && ownsTerminalActions() && !terminalTriggerIsRunningQueuedAi()"
                                    class="btn btn-sm edit-btn"
                                    @click="editLastUser"
                                    title="Editar último mensaje"
                                >
                                    <i class="anticon anticon-edit"></i> Editar mensaje
                                </button>
                            </div>
                        </div>
                    </div>
                    <div v-else-if="message.waitingForUser" class="context-request-notice" role="status" aria-live="polite">
                        <div class="context-request-notice__icon" aria-hidden="true"><i class="anticon anticon-file-search"></i></div>
                        <div>
                            <strong>{{ message.continuationNeedsAction
                                ? 'La continuación se detuvo'
                                : (message.resumingContext ? 'Estoy revisando los documentos' : 'Necesito documentos para continuar')
                            }}</strong>
                            <p>{{ message.continuationNeedsAction
                                ? 'Revisa el estado y las opciones de la continuación debajo de tu mensaje.'
                                : (message.resumingContext ? 'Continuaré la respuesta con los documentos que acabas de adjuntar.' : 'Revisa la solicitud de documentos que aparece debajo de tu mensaje.')
                            }}</p>
                        </div>
                    </div>
                    <div v-else-if="message.isLoading && message.text.length > 0" class="bot-response">
                        <md-block class="bot-response streaming" :data-message-viewport-owner="viewportMessageIdentity()" v-html="renderedText"></md-block>
                        <span class="streaming-indicator">
                            <i class="anticon anticon-loading anticon-spin" style="color: var(--theme-color); margin-left: 5px;"></i>
                        </span>
                    </div>
                    <template v-else>
                        <div v-if="documentResponseCompletedHere" class="document-response-ready-label" data-testid="document-response-ready-label">
                            <i class="anticon anticon-check-circle" aria-hidden="true"></i> Respuesta lista
                        </div>
                        <md-block class="bot-response" :data-message-viewport-owner="viewportMessageIdentity()" v-html="renderedText"></md-block>
                    </template>
                </div>
            </div>
            <slot name="after-response"></slot>
        </div>
    `
}
